import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
    createTheaterItem,
    createTheaterNote,
    createTheaterFolder,
    deleteTheaterItem,
    getGroup,
    getGroupMembers,
    getGroupMessages,
    getChatGroupPins,
    getChatGroupStats,
    markChatGroupRead,
    createGroupMessage,
    reactToChatMessage,
    getTheaterFolders,
    getTheaterItems,
    moveTheaterItem,
    uploadChatAttachment,
    uploadTheaterMedia,
    searchChatMessages,
    setChatGroupPin,
    getChatMuteSettings,
    updateChatMuteSettings,
    cancelChatMessage,
    uploadFile,
} from '@/lib/api';
import Hls from 'hls.js';
import { AtSign, Bookmark, ChevronDown, ChevronUp, Download, Image, MessageSquare, Music, Pin, RefreshCw, Search, Send, Smile, ThumbsUp, Video, X, Languages } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { getTheme } from '@/lib/themes';
import stampData from '@/data/stamps.json';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { TranslateModal } from '@/components/TranslateModal';
import { useI18n } from '@/lib/i18n';
import { getInputSurface, getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

interface ChatWindowProps {
    group: any;
    currentUser: any;
    jumpMessageId?: number | null;
    onJumpComplete?: () => void;
    onBackToList?: () => void;
    showBackButton?: boolean;
}

const EMOJI_CHOICES = ['❤️', '✨', '👍', '🔥', '👏', '😍', '🎉', '🌸', '💎', '🥂', '😎', '💡', '😊', '🌟', '🙌', '🕊️', '🍀', '☕️'];
const MAX_EMOJI_PREVIEW = 5;
const STAMP_PAGE_SIZE = 10;

type StampMeta = {
    id: number;
    src: string;
    phrases: string[];
    series: string;
};
const THEATER_TABS = [
    { key: 'note', label: 'NOTE' },
    { key: 'text', label: 'TEXT' },
    { key: 'image', label: 'IMAGE' },
    { key: 'media', label: 'MOVIE＆VOICE' },
] as const;

type DownloadImageFormat = 'png' | 'jpeg';

const DOWNLOAD_MIME: Record<DownloadImageFormat, string> = {
    png: 'image/png',
    jpeg: 'image/jpeg',
};

const DOWNLOAD_EXTENSION: Record<DownloadImageFormat, string> = {
    png: 'png',
    jpeg: 'jpeg',
};

const DOWNLOAD_FORMATS: DownloadImageFormat[] = ['png', 'jpeg'];

const resolveDownloadUrl = (url?: string | null) => {
    if (!url) return '';
    if (/^(https?:|blob:|data:)/i.test(url)) return url;
    return url.startsWith('/') ? url : `/${url}`;
};

const safeDownloadPart = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 80) || 'download';
};

const saveBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const canvasToBlob = (canvas: HTMLCanvasElement, format: DownloadImageFormat, quality = 0.92) =>
    new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to create image'));
            }
        }, DOWNLOAD_MIME[format], quality);
    });

const loadImageElement = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new window.Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = url;
    });

const wrapCanvasText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
    const lines: string[] = [];
    const paragraphs = text.replace(/\r\n/g, '\n').split('\n');
    paragraphs.forEach((paragraph) => {
        if (!paragraph) {
            lines.push('');
            return;
        }
        let line = '';
        Array.from(paragraph).forEach((char) => {
            const testLine = `${line}${char}`;
            if (line && ctx.measureText(testLine).width > maxWidth) {
                lines.push(line);
                line = char;
            } else {
                line = testLine;
            }
        });
        lines.push(line);
    });
    return lines;
};

const downloadImageUrlAs = async (sourceUrl: string, filenameBase: string, format: DownloadImageFormat) => {
    const resolvedUrl = resolveDownloadUrl(sourceUrl);
    if (!resolvedUrl) throw new Error('Image URL is empty');

    const response = await fetch(resolvedUrl, { credentials: 'include' });
    if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);

    const sourceBlob = await response.blob();
    const objectUrl = URL.createObjectURL(sourceBlob);
    try {
        const image = await loadImageElement(objectUrl);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) throw new Error('Image size is empty');

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas is not available');

        if (format === 'jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(image, 0, 0, width, height);

        const blob = await canvasToBlob(canvas, format);
        saveBlob(blob, `${safeDownloadPart(filenameBase)}.${DOWNLOAD_EXTENSION[format]}`);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

const downloadTextCardAsImage = async ({
    title,
    meta,
    text,
    filenameBase,
    format,
}: {
    title: string;
    meta: string;
    text: string;
    filenameBase: string;
    format: DownloadImageFormat;
}) => {
    const width = 900;
    const padding = 40;
    const bodyFont = '22px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const titleFont = 'bold 28px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const metaFont = '17px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) throw new Error('Canvas is not available');

    measureCtx.font = bodyFont;
    const lines = wrapCanvasText(measureCtx, text.trim() || ' ', width - padding * 2);
    const bodyLineHeight = 32;
    const height = Math.max(260, padding * 2 + 44 + 28 + lines.length * bodyLineHeight);
    const scale = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available');
    ctx.scale(scale, scale);

    ctx.fillStyle = format === 'jpeg' ? '#ffffff' : '#fffdf8';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#d8d0c3';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    let y = padding;
    ctx.fillStyle = '#2f2722';
    ctx.font = titleFont;
    ctx.fillText(title, padding, y + 28);
    y += 48;

    ctx.fillStyle = '#7a6a59';
    ctx.font = metaFont;
    ctx.fillText(meta, padding, y + 20);
    y += 46;

    ctx.fillStyle = '#2f2722';
    ctx.font = bodyFont;
    lines.forEach((line) => {
        ctx.fillText(line, padding, y);
        y += bodyLineHeight;
    });

    const blob = await canvasToBlob(canvas, format);
    saveBlob(blob, `${safeDownloadPart(filenameBase)}.${DOWNLOAD_EXTENSION[format]}`);
};

const HlsVideoPlayer = ({
    hlsSrc,
    fallbackSrc,
    className,
}: {
    hlsSrc: string | null;
    fallbackSrc: string | null;
    className?: string;
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let hls: Hls | null = null;
        const canNativeHls = !!video.canPlayType('application/vnd.apple.mpegurl');

        if (hlsSrc) {
            if (canNativeHls) {
                video.src = hlsSrc;
            } else if (Hls.isSupported()) {
                hls = new Hls();
                hls.loadSource(hlsSrc);
                hls.attachMedia(video);
                hls.on(Hls.Events.ERROR, () => {
                    if (fallbackSrc) {
                        video.src = fallbackSrc;
                    }
                });
            } else if (fallbackSrc) {
                video.src = fallbackSrc;
            }
        } else if (fallbackSrc) {
            video.src = fallbackSrc;
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
        };
    }, [hlsSrc, fallbackSrc]);

    return <video ref={videoRef} controls preload="metadata" className={className} />;
};

export function ChatWindow({ group, currentUser, jumpMessageId, onJumpComplete, onBackToList, showBackButton }: ChatWindowProps) {
    const navigate = useNavigate();
    const PAGE_SIZE = 50;
    const [messages, setMessages] = useState<any[]>([]);
    const messagesRef = useRef<any[]>([]);
    const [groupInfo, setGroupInfo] = useState<any>(group);
    const resolvedGroup = groupInfo?.group_id === group.group_id ? groupInfo : group;
    const [refreshingChat, setRefreshingChat] = useState(false);
    const activeGroupIdRef = useRef(group.group_id);
    const [input, setInput] = useState('');
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimerRef = useRef<number | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const wsConnectedRef = useRef(false);
    const closingRef = useRef(false);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionAll, setMentionAll] = useState(false);
    const [mentionUserIds, setMentionUserIds] = useState<Set<number>>(new Set());
    const mentionSnapshotRef = useRef<{
        mentionAll: boolean;
        mentionUserIds: Set<number>;
        mentionSearch: string;
    } | null>(null);
    const [groupMembers, setGroupMembers] = useState<any[]>([]);
    const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
    const [replyTo, setReplyTo] = useState<any | null>(null);
    const [reactionPickerFor, setReactionPickerFor] = useState<number | null>(null);
    const [reactionModal, setReactionModal] = useState<{ messageId: number; reactions: any[] } | null>(null);
    const [highlightMessageId, setHighlightMessageId] = useState<number | null>(null);
    const [viewMode, setViewMode] = useState<'chat' | 'theater'>('chat');
    const previousViewModeRef = useRef<'chat' | 'theater'>('chat');
    const [initialReadId, setInitialReadId] = useState<number | null>(null);
    const [initialScrollPending, setInitialScrollPending] = useState(false);
    const [initialScrollToLatest, setInitialScrollToLatest] = useState(false);
    const [attachment, setAttachment] = useState<{
        url: string;
        type: 'image' | 'video' | 'audio' | 'stamp';
        thumbUrl?: string | null;
        hlsUrl?: string | null;
        fallbackUrl?: string | null;
    } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [theaterTab, setTheaterTab] = useState<(typeof THEATER_TABS)[number]['key']>('note');
    const [theaterItems, setTheaterItems] = useState<any[]>([]);
    const [theaterLoading, setTheaterLoading] = useState(false);
    const [theaterFolders, setTheaterFolders] = useState<any[]>([]);
    const [theaterActiveFolderId, setTheaterActiveFolderId] = useState<number | null>(null);
    const [theaterFolderName, setTheaterFolderName] = useState('');
    const [theaterFolderParent, setTheaterFolderParent] = useState('root');
    const [theaterSaveFolder, setTheaterSaveFolder] = useState('none');
    const [theaterFilterTitle, setTheaterFilterTitle] = useState('');
    const [theaterFilterFrom, setTheaterFilterFrom] = useState('');
    const [theaterFilterTo, setTheaterFilterTo] = useState('');
    const [theaterAppliedFilters, setTheaterAppliedFilters] = useState({
        title: '',
        from: '',
        to: '',
    });
    const [theaterText, setTheaterText] = useState('');
    const [theaterUpload, setTheaterUpload] = useState<{
        url: string;
        contentType: string;
        thumbUrl?: string | null;
        hlsUrl?: string | null;
        fallbackUrl?: string | null;
    } | null>(null);
    const [theaterUploading, setTheaterUploading] = useState(false);
    const [theaterUploadError, setTheaterUploadError] = useState<string | null>(null);
    const [theaterUploadProgress, setTheaterUploadProgress] = useState<number | null>(null);
    const [theaterTextTitle, setTheaterTextTitle] = useState('');
    const [theaterMediaTitle, setTheaterMediaTitle] = useState('');
    const [stampOpen, setStampOpen] = useState(false);
    const [stampTab, setStampTab] = useState<'recent' | 'frequent' | 'pt'>('recent');
    const [stampPage, setStampPage] = useState(0);
    const [recentStampIds, setRecentStampIds] = useState<number[]>([]);
    const [frequentStampCounts, setFrequentStampCounts] = useState<Record<number, number>>({});
    const [stampSuggestions, setStampSuggestions] = useState<StampMeta[]>([]);
    const [stampSuggestionsExpanded, setStampSuggestionsExpanded] = useState(false);
    const [stampListExpanded, setStampListExpanded] = useState(false);
    const stampAutoOpenRef = useRef(false);
    const [noteStatus, setNoteStatus] = useState<string | null>(null);
    const [sendError, setSendError] = useState<string | null>(null);
    const [isPinned, setIsPinned] = useState(false);
    const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set());
    const [loadedTheaterVideos, setLoadedTheaterVideos] = useState<Set<number>>(new Set());
    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);
    const fetchingOlderRef = useRef(false);
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const loadOlderRef = useRef<() => void>(() => {});
    const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const lastReadMessageIdRef = useRef<number | null>(null);
    const markingReadRef = useRef(false);
    const anchorHoldUntilRef = useRef(0);
    const pendingAnchorRef = useRef<number | null>(null);
    const inlineJumpRef = useRef<number | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const audioInputRef = useRef<HTMLInputElement>(null);
    const theaterFileInputRef = useRef<HTMLInputElement>(null);
    const mentionInputRef = useRef<HTMLInputElement>(null);
    const chatInputRef = useRef<HTMLTextAreaElement>(null);
    const theaterTextRef = useRef<HTMLTextAreaElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [searchOpen, setSearchOpen] = useState(false);
    const [chatInputMaxHeight, setChatInputMaxHeight] = useState(240);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [muteOpen, setMuteOpen] = useState(false);
    const [muteAll, setMuteAll] = useState(false);
    const [muteNight, setMuteNight] = useState(false);
    const [mutedUserIds, setMutedUserIds] = useState<Set<number>>(new Set());
    const [muteSearch, setMuteSearch] = useState('');
    const [muteLoading, setMuteLoading] = useState(false);
    const [muteSaving, setMuteSaving] = useState(false);
    const [muteError, setMuteError] = useState('');
    const [translateOpen, setTranslateOpen] = useState(false);
    const [translateSource, setTranslateSource] = useState('');
    const { t } = useI18n();
    const chatTheme = getTheme(currentUser?.role_type || 'User', currentUser?.floor_level || 3);
    const darkTheme = isDarkTheme(chatTheme);
    const mutedTextColor = getMutedTextColor(chatTheme);
    const panelStyle = getPanelStyle(chatTheme, 0.97, 0.9);
    const softSurfaceColor = getSoftSurface(chatTheme, 0.84, 0.44);
    const inputSurface = getInputSurface(chatTheme);
    const primaryTextColor = pickTextColor(chatTheme.colors.primary);

    const getMessagePalette = (roleType: string | undefined, floorLevel: number | undefined, isOwn: boolean) => {
        const bubbleTheme = getTheme(roleType || 'User', floorLevel || 3);
        const bubbleDark = isDarkTheme(bubbleTheme);
        const eliteTheme = ['Admin', 'Maestro'].includes(roleType || '') || bubbleTheme.name === 'The Heaven';

        if (isOwn) {
            if (eliteTheme) {
                return {
                    backgroundColor: withAlpha(bubbleTheme.colors.card, 0.98),
                    borderColor: bubbleTheme.colors.border,
                    textColor: bubbleTheme.colors.text,
                    secondaryTextColor: withAlpha(bubbleTheme.colors.text, 0.78),
                    chipBackground: withAlpha(bubbleTheme.colors.primary, 0.18),
                    chipTextColor: bubbleTheme.colors.text,
                };
            }

            return {
                backgroundColor: bubbleTheme.colors.primary,
                borderColor: bubbleTheme.colors.border,
                textColor: pickTextColor(bubbleTheme.colors.primary),
                secondaryTextColor: withAlpha(pickTextColor(bubbleTheme.colors.primary), 0.82),
                chipBackground: withAlpha('#ffffff', 0.18),
                chipTextColor: pickTextColor(bubbleTheme.colors.primary),
            };
        }

        return {
            backgroundColor: eliteTheme
                ? withAlpha('#000000', 0.96)
                : withAlpha(bubbleTheme.colors.card, bubbleDark ? 0.94 : 0.98),
            borderColor: bubbleTheme.colors.border,
            textColor: eliteTheme ? '#f4e7c6' : bubbleTheme.colors.text,
            secondaryTextColor: eliteTheme ? withAlpha('#f4e7c6', 0.76) : getMutedTextColor(bubbleTheme),
            chipBackground: eliteTheme ? withAlpha('#d4af37', 0.16) : withAlpha(bubbleTheme.colors.primary, 0.12),
            chipTextColor: eliteTheme ? '#f4e7c6' : bubbleTheme.colors.primary,
        };
    };

    const draftKey = currentUser?.user_id && group?.group_id
        ? `maison_chat_draft_${currentUser.user_id}_${group.group_id}`
        : null;

    const baseScrollStyle: CSSProperties = {
        overscrollBehaviorY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
    };

    useEffect(() => {
        const updateMaxHeight = () => {
            const maxHeight = Math.max(140, Math.min(window.innerHeight * 0.3, 280));
            setChatInputMaxHeight(Math.round(maxHeight));
        };
        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        return () => window.removeEventListener('resize', updateMaxHeight);
    }, []);

    useEffect(() => {
        autoResizeTextarea(chatInputRef.current, chatInputMaxHeight);
    }, [input, chatInputMaxHeight]);

    useEffect(() => {
        autoResizeTextarea(theaterTextRef.current);
    }, [theaterText]);

    useEffect(() => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError('');
        setSearchLoading(false);
        setMuteOpen(false);
        setMuteAll(false);
        setMuteNight(false);
        setMutedUserIds(new Set());
        setMuteSearch('');
        setMuteError('');
        setMuteLoading(false);
        setMuteSaving(false);
        setSendError(null);
        setIsPinned(false);
    }, [group.group_id]);

    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const highlightText = (text: string) => {
        const term = searchQuery.trim();
        if (!term) return text;
        const escaped = escapeRegExp(term);
        const regex = new RegExp(escaped, 'ig');
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            if (start > lastIndex) {
                parts.push(text.slice(lastIndex, start));
            }
            parts.push(
                <mark key={`${start}-${end}`} className="bg-yellow-200 text-black rounded px-0.5">
                    {text.slice(start, end)}
                </mark>
            );
            lastIndex = end;
        }
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        return parts.length > 0 ? parts : text;
    };

    useEffect(() => {
        if (!searchOpen) return;
        const term = searchQuery.trim();
        if (!term) {
            setSearchResults([]);
            setSearchError('');
            return;
        }
        const timer = setTimeout(async () => {
            setSearchLoading(true);
            setSearchError('');
            try {
                const res = await searchChatMessages(term);
                const matches = Array.isArray(res.data?.matches) ? res.data.matches : [];
                const filtered = matches.filter((item: any) => item.group_id === group.group_id);
                setSearchResults(filtered);
            } catch (err: any) {
                console.error('Failed to search chat messages', err);
                setSearchError('検索に失敗しました。');
            } finally {
                setSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchOpen, searchQuery, group.group_id]);

    useEffect(() => {
        if (!muteOpen) return;
        const targetGroupId = group.group_id;
        setMuteLoading(true);
        setMuteError('');
        getChatMuteSettings(targetGroupId)
            .then((res) => {
                if (activeGroupIdRef.current !== targetGroupId) return;
                const data = res.data || {};
                setMuteAll(Boolean(data.mute_all));
                setMuteNight(Boolean(data.mute_night));
                const ids = Array.isArray(data.muted_user_ids) ? data.muted_user_ids : [];
                setMutedUserIds(new Set(ids.map((id: number) => Number(id))));
            })
            .catch((err) => {
                console.error('Failed to load mute settings', err);
                setMuteError('ミュート設定の取得に失敗しました。');
            })
            .finally(() => {
                if (activeGroupIdRef.current === targetGroupId) {
                    setMuteLoading(false);
                }
            });
    }, [muteOpen, group.group_id]);

    const isNearBottom = () => {
        if (!scrollRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        return scrollHeight - scrollTop - clientHeight < 120;
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
        }, 100);
    };

    const scrollToTop = () => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToEnd = () => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    };

    const jumpToMessage = async (messageId?: number | null) => {
        const targetId = messageId ? Number(messageId) : null;
        if (!targetId || !Number.isFinite(targetId)) return;
        const existing = messageRefs.current[targetId];
        if (existing) {
            existing.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightMessageId(targetId);
            setTimeout(() => setHighlightMessageId(null), 2000);
            return;
        }
        inlineJumpRef.current = targetId;
        anchorHoldUntilRef.current = Date.now() + 8000;
        try {
            await fetchMessages(true, targetId, true);
        } catch (err) {
            console.error('Failed to jump to message', err);
        }
    };

    const mergeMessages = (prev: any[], incoming: any[]) => {
        if (!incoming.length) return prev;
        const map = new Map<number, any>();
        for (const msg of prev) {
            if (msg?.message_id) {
                map.set(msg.message_id, msg);
            }
        }
        for (const msg of incoming) {
            if (!msg?.message_id) continue;
            const existing = map.get(msg.message_id);
            map.set(msg.message_id, existing ? { ...existing, ...msg } : msg);
        }
        return Array.from(map.values()).sort((a, b) => (a.message_id || 0) - (b.message_id || 0));
    };

    const getOldestMessageId = (items: any[]) => {
        let oldest = Number.POSITIVE_INFINITY;
        for (const item of items) {
            const id = Number(item?.message_id);
            if (Number.isFinite(id)) {
                oldest = Math.min(oldest, id);
            }
        }
        return Number.isFinite(oldest) ? oldest : null;
    };

    const appendMessage = (message: any, shouldScroll?: boolean) => {
        setMessages((prev) => {
            if (!message?.message_id) {
                return [...prev, message];
            }
            if (prev.some((item) => item.message_id === message.message_id)) {
                return prev;
            }
            return [...prev, message];
        });
        if (shouldScroll) {
            scrollToBottom();
        }
    };

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        activeGroupIdRef.current = group.group_id;
    }, [group.group_id]);

    useEffect(() => {
        inlineJumpRef.current = null;
    }, [group.group_id]);

    useEffect(() => {
        if (!draftKey || typeof window === 'undefined') return;
        const raw = localStorage.getItem(draftKey);
        if (!raw) {
            setInput('');
            setAttachment(null);
            setMentionAll(false);
            setMentionUserIds(new Set());
            setReplyTo(null);
            return;
        }
        try {
            const draft = JSON.parse(raw);
            setInput(typeof draft.input === 'string' ? draft.input : '');
            setAttachment(draft.attachment || null);
            const draftMentionAll = draft.mentionAll === true;
            setMentionAll(draftMentionAll);
            const draftMentions = draftMentionAll ? [] : Array.isArray(draft.mentionUserIds) ? draft.mentionUserIds : [];
            const mentionIds = draftMentions
                .map((id: any) => Number(id))
                .filter((id: number) => Number.isFinite(id));
            setMentionUserIds(new Set(mentionIds));
            const replyDraft = draft.replyTo;
            if (replyDraft && typeof replyDraft === 'object') {
                const replyId = Number(replyDraft.message_id);
                if (Number.isFinite(replyId)) {
                    setReplyTo({
                        message_id: replyId,
                        display_name: typeof replyDraft.display_name === 'string' ? replyDraft.display_name : '',
                        content: typeof replyDraft.content === 'string' ? replyDraft.content : '',
                    });
                } else {
                    setReplyTo(null);
                }
            } else {
                setReplyTo(null);
            }
        } catch (err) {
            console.warn('Failed to parse chat draft', err);
        }
    }, [draftKey]);

    useEffect(() => {
        if (!draftKey || typeof window === 'undefined') return;
        const hasMentions = mentionAll || mentionUserIds.size > 0;
        if (!input.trim() && !attachment && !hasMentions && !replyTo) {
            localStorage.removeItem(draftKey);
            return;
        }
        const mentionIds = mentionAll ? [] : Array.from(mentionUserIds);
        localStorage.setItem(
            draftKey,
            JSON.stringify({
                input,
                attachment,
                mentionAll,
                mentionUserIds: mentionIds,
                replyTo: replyTo
                    ? {
                          message_id: replyTo.message_id,
                          display_name: replyTo.display_name,
                          content: replyTo.content,
                      }
                    : null,
            })
        );
    }, [draftKey, input, attachment, mentionAll, mentionUserIds, replyTo]);

    const closeMentionModal = (mode: 'confirm' | 'cancel') => {
        if (mode === 'cancel' && mentionSnapshotRef.current) {
            const snapshot = mentionSnapshotRef.current;
            setMentionAll(snapshot.mentionAll);
            setMentionUserIds(new Set(snapshot.mentionUserIds));
            setMentionSearch(snapshot.mentionSearch);
        }
        setMentionOpen(false);
    };

    useEffect(() => {
        if (!mentionOpen) return;
        mentionSnapshotRef.current = {
            mentionAll,
            mentionUserIds: new Set(mentionUserIds),
            mentionSearch,
        };
        const focusInput = () => mentionInputRef.current?.focus();
        requestAnimationFrame(focusInput);
        const timer = window.setTimeout(focusInput, 50);
        return () => clearTimeout(timer);
    }, [mentionOpen]);

    useEffect(() => {
        if (!mentionOpen && !reactionModal) return;
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (mentionOpen) {
                    event.preventDefault();
                    closeMentionModal('cancel');
                }
                if (reactionModal) {
                    event.preventDefault();
                    setReactionModal(null);
                }
                return;
            }
            if (mentionOpen && event.key === 'Enter') {
                event.preventDefault();
                closeMentionModal('confirm');
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [mentionOpen, reactionModal]);

    const markGroupRead = async (latestMessageId?: number, forceLatest?: boolean) => {
        if (markingReadRef.current) return;
        if (!forceLatest && latestMessageId && lastReadMessageIdRef.current && latestMessageId <= lastReadMessageIdRef.current) {
            return;
        }
        markingReadRef.current = true;
        try {
            const res = await markChatGroupRead(group.group_id, forceLatest ? undefined : latestMessageId);
            const updatedId = res.data?.message_id;
            if (updatedId) {
                lastReadMessageIdRef.current = Number(updatedId);
            }
            window.dispatchEvent(new CustomEvent('maison:salon-read', { detail: { groupId: group.group_id } }));
        } catch (err) {
            console.error(err);
        } finally {
            markingReadRef.current = false;
        }
    };

    const fetchMessages = async (
        forceScroll = false,
        anchorMessageId?: number | null,
        forceMarkLatest = false
    ) => {
        const targetGroupId = group.group_id;
        if (isFetchingRef.current) {
            if (anchorMessageId) {
                pendingAnchorRef.current = anchorMessageId;
            }
            return;
        }
        isFetchingRef.current = true;
        try {
            const shouldScroll = forceScroll || isNearBottom();
            if (anchorMessageId) {
                const hadMessages = messagesRef.current.length > 0;
                const res = await getGroupMessages(targetGroupId, { anchor_message_id: anchorMessageId, limit: PAGE_SIZE });
                const nextMessages = res.data || [];
                if (activeGroupIdRef.current !== targetGroupId) {
                    return;
                }
                setMessages((prev) => (prev.length === 0 ? nextMessages : mergeMessages(prev, nextMessages)));
                if (!hadMessages) {
                    setHasMoreMessages(nextMessages.length === PAGE_SIZE);
                }
                if (shouldScroll) {
                    scrollToBottom();
                }
                const latestMessageId = nextMessages.length ? nextMessages[nextMessages.length - 1].message_id : undefined;
                markGroupRead(latestMessageId, forceMarkLatest || Boolean(anchorMessageId));
                return;
            }

            const res = await getGroupMessages(targetGroupId, { skip: 0, limit: PAGE_SIZE });
            const nextMessages = res.data || [];
            if (activeGroupIdRef.current !== targetGroupId) {
                return;
            }
            if (messagesRef.current.length === 0) {
                setMessages(nextMessages);
                setHasMoreMessages(nextMessages.length === PAGE_SIZE);
            } else {
                setMessages((prev) => mergeMessages(prev, nextMessages));
            }
            if (shouldScroll) {
                scrollToBottom();
            }
            const latestMessageId = nextMessages.length ? nextMessages[nextMessages.length - 1].message_id : undefined;
            markGroupRead(latestMessageId, forceMarkLatest);
        } finally {
            isFetchingRef.current = false;
            if (pendingAnchorRef.current) {
                if (activeGroupIdRef.current === targetGroupId) {
                    const pendingAnchor = pendingAnchorRef.current;
                    pendingAnchorRef.current = null;
                    fetchMessages(true, pendingAnchor, true);
                } else {
                    pendingAnchorRef.current = null;
                }
            }
        }
    };

    const loadOlderMessages = async () => {
        if (loadingOlder || fetchingOlderRef.current || !hasMoreMessages) return;
        if (!scrollRef.current) return;
        const targetGroupId = group.group_id;
        setLoadingOlder(true);
        fetchingOlderRef.current = true;
        const beforeHeight = scrollRef.current.scrollHeight;
        try {
            const oldestMessageId = getOldestMessageId(messagesRef.current);
            const params = oldestMessageId
                ? { before_message_id: oldestMessageId, limit: PAGE_SIZE }
                : { skip: messagesRef.current.length, limit: PAGE_SIZE };
            const res = await getGroupMessages(targetGroupId, params);
            const olderMessages = res.data || [];
            if (activeGroupIdRef.current !== targetGroupId) {
                return;
            }
            if (!olderMessages.length) {
                setHasMoreMessages(false);
                return;
            }
            setMessages((prev) => mergeMessages(olderMessages, prev));
            setHasMoreMessages(olderMessages.length === PAGE_SIZE);
        } finally {
            requestAnimationFrame(() => {
                if (scrollRef.current) {
                    const afterHeight = scrollRef.current.scrollHeight;
                    scrollRef.current.scrollTop += afterHeight - beforeHeight;
                }
            });
            fetchingOlderRef.current = false;
            setLoadingOlder(false);
        }
    };

    useEffect(() => {
        loadOlderRef.current = loadOlderMessages;
    }, [loadOlderMessages]);

    const handleScroll = () => {
        if (initialScrollPending || initialScrollToLatest) return;
        if (!scrollRef.current || loadingOlder || !hasMoreMessages) return;
        if (scrollRef.current.scrollTop <= 60) {
            loadOlderMessages();
        }
    };

    const handleRefreshChat = async () => {
        if (refreshingChat) return;
        setRefreshingChat(true);
        const targetGroupId = group.group_id;
        try {
            setHasMoreMessages(true);
            messagesRef.current = [];
            setMessages([]);
            const [groupRes, membersRes] = await Promise.allSettled([
                getGroup(targetGroupId),
                getGroupMembers(targetGroupId),
            ]);
            if (groupRes.status === 'fulfilled' && groupRes.value?.data) {
                if (groupRes.value.data?.group_id === targetGroupId) {
                    setGroupInfo(groupRes.value.data);
                }
            }
            if (membersRes.status === 'fulfilled') {
                if (activeGroupIdRef.current === targetGroupId) {
                    setGroupMembers(membersRes.value?.data || []);
                }
            } else {
                if (activeGroupIdRef.current === targetGroupId) {
                    setGroupMembers([]);
                }
            }
            await fetchMessages(true, null, true);
        } finally {
            setRefreshingChat(false);
        }
    };

    useEffect(() => {
        const targetGroupId = group.group_id;
        setGroupInfo(group);
        getGroup(targetGroupId)
            .then((res) => {
                if (res.data && res.data.group_id === targetGroupId && activeGroupIdRef.current === targetGroupId) {
                    setGroupInfo(res.data);
                }
            })
            .catch(() => {});
    }, [group.group_id]);

    useEffect(() => {
        if (viewMode !== 'chat') return;
        const target = topSentinelRef.current;
        if (!target) return;
        const rootCandidate = scrollRef.current;
        const root = rootCandidate && rootCandidate.scrollHeight > rootCandidate.clientHeight + 1
            ? rootCandidate
            : null;
        const observer = new IntersectionObserver(
            (entries) => {
                if (initialScrollPending || initialScrollToLatest) return;
                if (entries.some((entry) => entry.isIntersecting)) {
                    loadOlderRef.current();
                }
            },
            { root, rootMargin: '80px 0px 0px 0px', threshold: 0 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [group.group_id, viewMode, messages.length, initialScrollPending, initialScrollToLatest]);

    useEffect(() => {
        const targetGroupId = group.group_id;
        lastReadMessageIdRef.current = null;
        anchorHoldUntilRef.current = 0;
        pendingAnchorRef.current = null;
        wsConnectedRef.current = false;
        closingRef.current = false;
        messagesRef.current = [];
        setMessages([]);
        setHasMoreMessages(true);
        setLoadingOlder(false);
        setInitialReadId(null);
        setInitialScrollPending(false);
        setInitialScrollToLatest(false);

        const initializeMessages = async () => {
            if (jumpMessageId) return;
            try {
                const statsRes = await getChatGroupStats();
                if (activeGroupIdRef.current !== targetGroupId) return;
                const stat = statsRes.data?.groups?.[group.group_id];
                const lastReadId = Number(stat?.last_read_message_id || 0);
                const unreadCount = Number(stat?.unread_count || 0);
                if (lastReadId && unreadCount > 0) {
                    setInitialReadId(lastReadId);
                    setInitialScrollPending(true);
                    setInitialScrollToLatest(false);
                    try {
                        await fetchMessages(true, lastReadId, true);
                        return;
                    } catch (err) {
                        console.warn('Failed to load unread anchor', err);
                    }
                }
            } catch (err) {
                console.warn('Failed to load chat stats', err);
            }
            setInitialScrollToLatest(true);
            fetchMessages(true, null, true);
        };

        initializeMessages();

        getGroupMembers(group.group_id).then(res => {
            if (activeGroupIdRef.current !== targetGroupId) return;
            setGroupMembers(res.data || []);
        }).catch(() => {
            if (activeGroupIdRef.current !== targetGroupId) return;
            setGroupMembers([]);
        });

        const setupWebSocket = () => {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }
            const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
            const wsUrl = `${wsProtocol}://${window.location.host}/api/chat/${group.group_id}/ws?token=${token}`;
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                reconnectAttemptsRef.current = 0;
                wsConnectedRef.current = true;
                fetchMessages(false);
            };

            ws.onmessage = (event) => {
                const payload = JSON.parse(event.data);
                if (payload.type === 'message_deleted') {
                    setMessages((prev) =>
                        prev.map((msg) =>
                            msg.message_id === payload.message_id
                                ? {
                                      ...msg,
                                      is_deleted: true,
                                      content: '送信を取り消しました',
                                      attachment_url: null,
                                      attachment_thumb_url: null,
                                      attachment_hls_url: null,
                                      attachment_fallback_url: null,
                                  }
                                : msg
                        )
                    );
                    return;
                }
                if (payload.type === 'reaction') {
                    setMessages((prev) => prev.map((msg) => (
                        msg.message_id === payload.message_id ? { ...msg, reactions: payload.reactions } : msg
                    )));
                    return;
                }
                const shouldScroll = isNearBottom();
                appendMessage(payload, shouldScroll);
            };

            ws.onerror = () => {
                ws.close();
            };

            ws.onclose = () => {
                wsConnectedRef.current = false;
                wsRef.current = null;
                if (closingRef.current) return;
                const delay = Math.min(15000, 1000 * Math.pow(2, Math.min(4, reconnectAttemptsRef.current++)));
                if (reconnectTimerRef.current) {
                    clearTimeout(reconnectTimerRef.current);
                }
                reconnectTimerRef.current = window.setTimeout(() => {
                    setupWebSocket();
                }, delay);
            };
        };

        setupWebSocket();

        const interval = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            if (Date.now() < anchorHoldUntilRef.current) return;
            if (wsConnectedRef.current) return;
            fetchMessages(false);
        }, 5000);

        return () => {
            closingRef.current = true;
            wsConnectedRef.current = false;
            clearInterval(interval);
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (e) { /* noop */ }
            }
        };
    }, [group.group_id, jumpMessageId]);

    useEffect(() => {
        if (!jumpMessageId) return;
        inlineJumpRef.current = null;
        anchorHoldUntilRef.current = Date.now() + 8000;
        fetchMessages(true, jumpMessageId, true);
    }, [jumpMessageId, group.group_id]);

    useEffect(() => {
        if (!initialScrollPending || initialReadId === null) return;
        if (jumpMessageId) return;
        if (!messages.length) return;
        const firstUnread = messages.find((msg) => Number(msg?.message_id) > initialReadId);
        const fallbackId = messages[messages.length - 1]?.message_id;
        const targetId = firstUnread?.message_id ?? initialReadId ?? fallbackId;
        if (!targetId) return;
        const target = messageRefs.current[targetId];
        if (!target) return;
        target.scrollIntoView({ behavior: 'auto', block: 'center' });
        setHighlightMessageId(targetId);
        setTimeout(() => setHighlightMessageId(null), 2000);
        setInitialScrollPending(false);
    }, [messages, initialScrollPending, initialReadId, jumpMessageId]);

    useEffect(() => {
        if (!initialScrollToLatest) return;
        if (jumpMessageId) return;
        if (!messages.length) return;
        const container = scrollRef.current;
        const content = contentRef.current;
        const lastMessageId = messages[messages.length - 1]?.message_id;
        const lastMessageEl = lastMessageId ? messageRefs.current[lastMessageId] : null;
        if (!container || !content) {
            if (lastMessageEl) {
                lastMessageEl.scrollIntoView({ behavior: 'auto', block: 'end' });
            } else {
                scrollToBottom();
            }
            setInitialScrollToLatest(false);
            return;
        }
        let rafId = 0;
        const scrollNow = () => {
            if (lastMessageEl) {
                lastMessageEl.scrollIntoView({ behavior: 'auto', block: 'end' });
                return;
            }
            if (!scrollRef.current) return;
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        };
        scrollNow();
        rafId = requestAnimationFrame(scrollNow);
        const resizeObserver = new ResizeObserver(scrollNow);
        resizeObserver.observe(content);
        const timer = window.setTimeout(() => {
            resizeObserver.disconnect();
            setInitialScrollToLatest(false);
        }, 1200);
        return () => {
            resizeObserver.disconnect();
            cancelAnimationFrame(rafId);
            clearTimeout(timer);
        };
    }, [messages, initialScrollToLatest, jumpMessageId]);

    useEffect(() => {
        if (viewMode !== 'theater') return;
        fetchTheaterItems();
    }, [
        viewMode,
        theaterTab,
        group.group_id,
        theaterActiveFolderId,
        theaterAppliedFilters.title,
        theaterAppliedFilters.from,
        theaterAppliedFilters.to,
    ]);

    useEffect(() => {
        if (previousViewModeRef.current !== viewMode && viewMode === 'chat') {
            fetchMessages(false);
        }
        previousViewModeRef.current = viewMode;
    }, [viewMode, group.group_id]);

    useEffect(() => {
        if (viewMode !== 'theater') return;
        fetchTheaterFolders();
    }, [viewMode, group.group_id]);

    useEffect(() => {
        setTheaterUpload(null);
        setTheaterMediaTitle('');
        setTheaterTextTitle('');
    }, [theaterTab]);

    useEffect(() => {
        if (!jumpMessageId) return;
        const target = messageRefs.current[jumpMessageId];
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightMessageId(jumpMessageId);
            setTimeout(() => setHighlightMessageId(null), 2000);
            if (onJumpComplete) onJumpComplete();
        }
    }, [jumpMessageId, messages, onJumpComplete]);

    useEffect(() => {
        if (jumpMessageId) return;
        const targetId = inlineJumpRef.current;
        if (!targetId) return;
        const target = messageRefs.current[targetId];
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightMessageId(targetId);
        setTimeout(() => setHighlightMessageId(null), 2000);
        inlineJumpRef.current = null;
    }, [messages]);

    const isStaff = ['Admin', 'Manager', 'Maestro'].includes(currentUser.role_type);
    const isFamiglia = currentUser.role_type === 'Famiglia';
    const canTheaterPost = isStaff || isFamiglia;
    const groupType = (group?.group_type || 'normal').toLowerCase();
    const isDirectGroup = groupType === 'direct';
    const isAnnouncementGroup = groupType === 'announcement';
    const canPostChat = !isAnnouncementGroup || isStaff;
    const canUseTheater = !isDirectGroup;
    const canEmoji = true;
    const directPeerUserId = Number.isFinite(Number(resolvedGroup?.direct_peer_user_id))
        ? Number(resolvedGroup.direct_peer_user_id)
        : null;
    const resolveName = (displayName?: string) => (displayName && displayName.trim() ? displayName : '住人');
    const memberLookup = useMemo(() => {
        const map = new Map<number, { display_name?: string; username?: string }>();
        groupMembers.forEach((member) => {
            map.set(member.user_id, {
                display_name: member.display_name,
                username: member.username,
            });
        });
        return map;
    }, [groupMembers]);

    const resolveMentionLabel = (userId: number) => {
        const member = memberLookup.get(userId);
        const display = member?.display_name?.trim();
        const label = display || '住人';
        return `@${label}`;
    };

    const mentionUserFromMessage = (userId?: number | null) => {
        const targetId = Number(userId);
        if (!Number.isFinite(targetId)) return;
        if (targetId === Number(currentUser?.user_id)) return;
        setMentionAll(false);
        setMentionUserIds((prev) => {
            const next = new Set(prev);
            next.add(targetId);
            return next;
        });
        requestAnimationFrame(() => chatInputRef.current?.focus());
    };

    const handleResidentProfileNavigate = (
        userId?: number | null,
        event?: Pick<MouseEvent<HTMLElement>, 'stopPropagation'> | null
    ) => {
        if (!userId) return;
        event?.stopPropagation?.();
        navigate(`/users/${userId}`);
    };

    const formatChatTimestamp = (value: any) => {
        const ts = parseCreatedAt(value);
        if (!ts) return '';
        const date = new Date(ts);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
    };

    const stampList = useMemo(() => stampData as StampMeta[], []);
    const stampMap = useMemo(() => {
        const map = new Map<number, StampMeta>();
        stampList.forEach((stamp) => map.set(stamp.id, stamp));
        return map;
    }, [stampList]);
    const stampRecentKey = currentUser?.user_id ? `maison_stamp_recent_${currentUser.user_id}` : null;
    const stampFreqKey = currentUser?.user_id ? `maison_stamp_freq_${currentUser.user_id}` : null;

    useEffect(() => {
        if (!stampRecentKey || !stampFreqKey) return;
        try {
            const recentRaw = localStorage.getItem(stampRecentKey);
            const freqRaw = localStorage.getItem(stampFreqKey);
            if (recentRaw) {
                const parsed = JSON.parse(recentRaw);
                if (Array.isArray(parsed)) {
                    setRecentStampIds(parsed.map((v) => Number(v)).filter((v) => Number.isFinite(v)));
                }
            } else {
                setRecentStampIds([]);
            }
            if (freqRaw) {
                const parsed = JSON.parse(freqRaw);
                if (parsed && typeof parsed === 'object') {
                    const next: Record<number, number> = {};
                    Object.entries(parsed).forEach(([key, value]) => {
                        const id = Number(key);
                        const count = Number(value);
                        if (Number.isFinite(id) && Number.isFinite(count)) {
                            next[id] = count;
                        }
                    });
                    setFrequentStampCounts(next);
                }
            } else {
                setFrequentStampCounts({});
            }
        } catch (err) {
            setRecentStampIds([]);
            setFrequentStampCounts({});
        }
    }, [stampRecentKey, stampFreqKey]);

    useEffect(() => {
        if (!stampRecentKey) return;
        localStorage.setItem(stampRecentKey, JSON.stringify(recentStampIds));
    }, [recentStampIds, stampRecentKey]);

    useEffect(() => {
        if (!stampFreqKey) return;
        localStorage.setItem(stampFreqKey, JSON.stringify(frequentStampCounts));
    }, [frequentStampCounts, stampFreqKey]);

    useEffect(() => {
        if (!sendError) return;
        setSendError(null);
    }, [attachment, input]);

    useEffect(() => {
        if (isDirectGroup && viewMode !== 'chat') {
            setViewMode('chat');
        }
    }, [isDirectGroup, viewMode]);

    useEffect(() => {
        const targetGroupId = group.group_id;
        getChatGroupPins()
            .then((res) => {
                if (activeGroupIdRef.current !== targetGroupId) return;
                const groupIds = Array.isArray(res.data?.group_ids) ? res.data.group_ids : [];
                setIsPinned(groupIds.map((id: number) => Number(id)).includes(targetGroupId));
            })
            .catch((err) => {
                console.error('Failed to load chat pins', err);
            });
    }, [group.group_id]);

    const normalizeStampText = (value: string) => {
        if (!value) return '';
        let text = value.normalize('NFKC').toLowerCase();
        text = text.replace(/[！!？?。．、,，…・\s]+/g, '');
        text = text.replace(/[\u30a1-\u30f6]/g, (char) => {
            const code = char.charCodeAt(0) - 0x60;
            return String.fromCharCode(code);
        });
        return text;
    };

    const stampIndex = useMemo(() => {
        return stampList.map((stamp) => ({
            stamp,
            normalizedPhrases: (stamp.phrases || []).map((phrase) => normalizeStampText(phrase)),
        }));
    }, [stampList]);

    useEffect(() => {
        const normalizedInput = normalizeStampText(input);
        if (!normalizedInput) {
            setStampSuggestions([]);
            if (stampAutoOpenRef.current) {
                setStampOpen(false);
                stampAutoOpenRef.current = false;
            }
            return;
        }
        const matches: StampMeta[] = [];
        stampIndex.forEach(({ stamp, normalizedPhrases }) => {
            if (
                normalizedPhrases.some((phrase) => phrase && phrase.startsWith(normalizedInput))
            ) {
                matches.push(stamp);
            }
        });
        setStampSuggestions(matches);
        if (matches.length > 0 && !stampOpen) {
            setStampSuggestionsExpanded(false);
            setStampListExpanded(false);
            setStampOpen(true);
            stampAutoOpenRef.current = true;
        }
        if (matches.length === 0 && stampAutoOpenRef.current) {
            setStampOpen(false);
            stampAutoOpenRef.current = false;
        }
    }, [input, stampIndex, stampOpen]);

    const recentStamps = useMemo(() => {
        return recentStampIds
            .map((id) => stampMap.get(id))
            .filter((stamp): stamp is StampMeta => Boolean(stamp));
    }, [recentStampIds, stampMap]);

    const frequentStamps = useMemo(() => {
        const entries = Object.entries(frequentStampCounts)
            .map(([key, value]) => ({ id: Number(key), count: Number(value) }))
            .filter((entry) => Number.isFinite(entry.id) && Number.isFinite(entry.count));
        entries.sort((a, b) => b.count - a.count || a.id - b.id);
        return entries
            .map((entry) => stampMap.get(entry.id))
            .filter((stamp): stamp is StampMeta => Boolean(stamp));
    }, [frequentStampCounts, stampMap]);

    const ptStamps = useMemo(() => {
        return stampList.filter((stamp) => stamp.series === 'PT');
    }, [stampList]);

    const muteCandidates = useMemo(() => {
        return groupMembers.filter((member) => member.user_id !== currentUser.user_id);
    }, [groupMembers, currentUser.user_id]);

    const filteredMuteMembers = useMemo(() => {
        if (!muteSearch.trim()) return muteCandidates;
        const term = muteSearch.toLowerCase();
        return muteCandidates.filter((member) =>
            resolveName(member.display_name).toLowerCase().includes(term)
        );
    }, [muteCandidates, muteSearch]);

    const toggleMutedUser = (userId: number) => {
        setMutedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const activeStampList = useMemo(() => {
        if (stampTab === 'recent') return recentStamps;
        if (stampTab === 'frequent') return frequentStamps;
        return ptStamps;
    }, [stampTab, recentStamps, frequentStamps, ptStamps]);

    const stampPageCount = Math.max(1, Math.ceil(activeStampList.length / STAMP_PAGE_SIZE));
    const clampedStampPage = Math.min(stampPage, stampPageCount - 1);
    const stampPageItems = activeStampList.slice(
        clampedStampPage * STAMP_PAGE_SIZE,
        clampedStampPage * STAMP_PAGE_SIZE + STAMP_PAGE_SIZE
    );

    useEffect(() => {
        if (stampPage !== clampedStampPage) {
            setStampPage(clampedStampPage);
        }
    }, [clampedStampPage, stampPage]);

    const sendViaHttp = async (payload: any) => {
        const res = await createGroupMessage(group.group_id, payload);
        const data = res.data;
        appendMessage(data, true);
    };

    const toggleGroupPin = async () => {
        const nextPinned = !isPinned;
        setIsPinned(nextPinned);
        try {
            await setChatGroupPin(group.group_id, nextPinned);
            window.dispatchEvent(new CustomEvent('maison:chat-pin-changed', { detail: { groupId: group.group_id, pinned: nextPinned } }));
        } catch (err) {
            console.error('Failed to update group pin', err);
            setIsPinned(!nextPinned);
            alert('ピン設定の保存に失敗しました。');
        }
    };

    const handleSelectStamp = (stamp: StampMeta) => {
        setAttachment({ url: stamp.src, type: 'stamp' });
        setStampOpen(false);
        stampAutoOpenRef.current = false;
        setRecentStampIds((prev) => {
            const next = [stamp.id, ...prev.filter((id) => id !== stamp.id)];
            return next.slice(0, 30);
        });
        setFrequentStampCounts((prev) => ({
            ...prev,
            [stamp.id]: (prev[stamp.id] || 0) + 1,
        }));
        requestAnimationFrame(() => chatInputRef.current?.focus());
    };

    const handleSend = async () => {
        if (!canPostChat) return;
        const trimmed = input.trim();
        if (uploading) return;
        if (!trimmed && !attachment) return;
        setSendError(null);
        const payload = {
            type: 'message',
            content: trimmed,
            mention_all: mentionAll,
            mentions: mentionAll ? [] : Array.from(mentionUserIds),
            reply_to_message_id: replyTo?.message_id || null,
            attachment_url: attachment?.url || null,
            attachment_type: attachment?.type || null,
            attachment_thumb_url: attachment?.thumbUrl || null,
            attachment_hls_url: attachment?.hlsUrl || null,
            attachment_fallback_url: attachment?.fallbackUrl || null,
        };
        try {
            await sendViaHttp(payload);
        } catch (err: any) {
            console.error('Failed to send message', err);
            const detail = err?.response?.data?.detail;
            setSendError(typeof detail === 'string' && detail.trim() ? detail : '送信に失敗しました。接続を確認してください。');
            return;
        }
        setInput('');
        setAttachment(null);
        setMentionAll(false);
        setMentionUserIds(new Set());
        setReplyTo(null);
        setStampOpen(false);
        if (draftKey && typeof window !== 'undefined') {
            localStorage.removeItem(draftKey);
        }
        setTimeout(() => {
            fetchMessages(false);
        }, 300);
    };

    const handleAttachmentUpload = async (file: File) => {
        setUploading(true);
        setUploadError(null);
        setUploadProgress(0);
        setStampOpen(false);
        try {
            const res = await uploadChatAttachment(file, group.group_id, (percent) => {
                setUploadProgress(percent);
            });
            const url = res.data?.url;
            const type = res.data?.type;
            if (url && (type === 'image' || type === 'video' || type === 'audio')) {
                setAttachment({
                    url,
                    type,
                    thumbUrl: res.data?.thumb_url,
                    hlsUrl: res.data?.hls_url,
                    fallbackUrl: res.data?.fallback_url,
                });
            }
        } catch (err: any) {
            console.error(err);
            const detail = err?.response?.data?.detail;
            setUploadError(detail ? `アップロードに失敗しました: ${detail}` : 'アップロードに失敗しました。');
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const resolveFolderSaveId = () => {
        if (theaterSaveFolder === 'none') return null;
        if (theaterSaveFolder.startsWith('id:')) {
            const parsed = Number(theaterSaveFolder.replace('id:', ''));
            return Number.isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    const resolveActiveFolderId = () => {
        if (theaterActiveFolderId === null) return 0;
        return theaterActiveFolderId;
    };

    const parseFolderValue = (value: string) => {
        if (value === 'none') return null;
        if (value.startsWith('id:')) {
            const parsed = Number(value.replace('id:', ''));
            return Number.isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    const fetchTheaterItems = async () => {
        setTheaterLoading(true);
        try {
            const typeParam = theaterTab === 'media' ? 'media' : theaterTab;
            const hasFilters = Boolean(
                theaterAppliedFilters.title || theaterAppliedFilters.from || theaterAppliedFilters.to
            );
            const params: Record<string, any> = { type: typeParam };
            if (theaterAppliedFilters.title) params.title = theaterAppliedFilters.title;
            if (theaterAppliedFilters.from) params.from_date = theaterAppliedFilters.from;
            if (theaterAppliedFilters.to) params.to_date = theaterAppliedFilters.to;
            if (!hasFilters) {
                params.folder_id = resolveActiveFolderId();
            }
            const res = await getTheaterItems(group.group_id, params);
            setTheaterItems(res.data || []);
        } catch (err) {
            console.error(err);
            setTheaterItems([]);
        } finally {
            setTheaterLoading(false);
        }
    };

    const fetchTheaterFolders = async () => {
        try {
            const res = await getTheaterFolders(group.group_id);
            setTheaterFolders(res.data || []);
        } catch (err) {
            console.error(err);
            setTheaterFolders([]);
        }
    };

    const handleTheaterTextSubmit = async () => {
        if (!theaterText.trim()) return;
        try {
            await createTheaterItem(group.group_id, {
                item_type: 'text',
                title: theaterTextTitle.trim() || undefined,
                folder_id: resolveFolderSaveId(),
                content_text: theaterText.trim(),
            });
            setTheaterText('');
            setTheaterTextTitle('');
            fetchTheaterItems();
        } catch (err) {
            console.error(err);
            alert('投稿に失敗しました。');
        }
    };

    const handleTheaterTextPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, theaterText, `\n${url}\n`, setTheaterText);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
        }
    };

    const handleTheaterUpload = async (file: File) => {
        setTheaterUploading(true);
        setTheaterUploadError(null);
        setTheaterUploadProgress(0);
        try {
            const res = await uploadTheaterMedia(file, (percent) => {
                setTheaterUploadProgress(percent);
            });
            const url = res.data?.url;
            const contentType = res.data?.type;
            if (url && contentType) {
                setTheaterUpload({
                    url,
                    contentType,
                    thumbUrl: res.data?.thumb_url,
                    hlsUrl: res.data?.hls_url,
                    fallbackUrl: res.data?.fallback_url,
                });
            }
        } catch (err: any) {
            console.error(err);
            const detail = err?.response?.data?.detail;
            setTheaterUploadError(detail ? `アップロードに失敗しました: ${detail}` : 'アップロードに失敗しました。');
        } finally {
            setTheaterUploading(false);
            setTheaterUploadProgress(null);
        }
    };

    const handleTheaterMediaSubmit = async () => {
        if (!theaterUpload) return;
        const isAudio = theaterUpload.contentType?.startsWith('audio/');
        const itemType = isAudio ? 'voice' : 'movie';
        try {
            await createTheaterItem(group.group_id, {
                item_type: theaterTab === 'image' ? 'image' : itemType,
                title: theaterMediaTitle.trim() || undefined,
                folder_id: resolveFolderSaveId(),
                media_url: theaterUpload.url,
                thumb_url: theaterUpload.thumbUrl || null,
                hls_url: theaterUpload.hlsUrl || null,
                fallback_url: theaterUpload.fallbackUrl || null,
            });
            setTheaterUpload(null);
            setTheaterMediaTitle('');
            fetchTheaterItems();
        } catch (err) {
            console.error(err);
            alert('投稿に失敗しました。');
        }
    };

    const applyTheaterFilters = () => {
        setTheaterAppliedFilters({
            title: theaterFilterTitle.trim(),
            from: theaterFilterFrom,
            to: theaterFilterTo,
        });
    };

    const resetTheaterFilters = () => {
        setTheaterFilterTitle('');
        setTheaterFilterFrom('');
        setTheaterFilterTo('');
        setTheaterAppliedFilters({ title: '', from: '', to: '' });
    };

    const handleCreateFolder = async () => {
        if (!theaterFolderName.trim()) return;
        const parentId = theaterFolderParent === 'root' ? null : parseFolderValue(theaterFolderParent);
        try {
            await createTheaterFolder(group.group_id, {
                name: theaterFolderName.trim(),
                parent_id: parentId,
            });
            setTheaterFolderName('');
            setTheaterFolderParent('root');
            fetchTheaterFolders();
        } catch (err) {
            console.error(err);
            alert('フォルダ作成に失敗しました。');
        }
    };

    const handleMoveItem = async (itemId: number, value: string) => {
        const folderId = parseFolderValue(value);
        try {
            await moveTheaterItem(itemId, folderId);
            fetchTheaterItems();
        } catch (err) {
            console.error(err);
            alert('フォルダ移動に失敗しました。');
        }
    };

    const handleDeleteItem = async (itemId: number) => {
        if (!confirm('この投稿を削除しますか？')) return;
        try {
            await deleteTheaterItem(itemId);
            fetchTheaterItems();
        } catch (err) {
            console.error(err);
            alert('削除に失敗しました。');
        }
    };

    const handleNote = async (messageId: number) => {
        if (!isStaff) return;
        try {
            await createTheaterNote(group.group_id, messageId);
            setNoteStatus('ノートに保存しました。');
            if (viewMode === 'theater') {
                fetchTheaterItems();
            }
        } catch (err) {
            console.error(err);
            setNoteStatus('ノートへの保存に失敗しました。');
        } finally {
            setTimeout(() => setNoteStatus(null), 2000);
        }
    };

    const toggleMentionUser = (userId: number) => {
        setMentionUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const filteredMembers = useMemo(() => {
        const query = mentionSearch.trim().toLowerCase();
        return groupMembers.filter((member) => {
            if (member.user_id === currentUser.user_id) return false;
            const display = (member.display_name || '').toLowerCase();
            return !query || display.includes(query);
        });
    }, [groupMembers, mentionSearch, currentUser.user_id]);

    const selectedMentionUsers = useMemo(() => {
        return groupMembers.filter((member) => mentionUserIds.has(member.user_id));
    }, [groupMembers, mentionUserIds]);

    const folderMap = useMemo(() => {
        return new Map(theaterFolders.map((folder) => [folder.folder_id, folder]));
    }, [theaterFolders]);

    const getFolderPath = (folderId?: number | null) => {
        if (!folderId) return '未分類';
        const parts: string[] = [];
        const visited = new Set<number>();
        let current = folderMap.get(folderId);
        while (current && !visited.has(current.folder_id)) {
            parts.push(current.name);
            visited.add(current.folder_id);
            current = current.parent_id ? folderMap.get(current.parent_id) : null;
        }
        return parts.reverse().join(' / ') || '未分類';
    };

    const folderOptions = useMemo(() => {
        const options = theaterFolders.map((folder) => ({
            value: `id:${folder.folder_id}`,
            label: getFolderPath(folder.folder_id),
        }));
        return options.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    }, [theaterFolders, folderMap]);

    const currentFolder = useMemo(() => {
        if (theaterActiveFolderId === null) return null;
        return folderMap.get(theaterActiveFolderId) || null;
    }, [theaterActiveFolderId, folderMap]);

    const currentSubfolders = useMemo(() => {
        return theaterFolders.filter((folder) => folder.parent_id === theaterActiveFolderId);
    }, [theaterFolders, theaterActiveFolderId]);

    const parseCreatedAt = (value: any) => {
        if (!value) return Date.now();
        if (typeof value === 'string') {
            const normalized = value.endsWith('Z') ? value : `${value}Z`;
            return new Date(normalized).getTime();
        }
        try {
            return new Date(value).getTime();
        } catch {
            return Date.now();
        }
    };

    const hasActiveTheaterFilters = useMemo(() => {
        return Boolean(
            theaterAppliedFilters.title || theaterAppliedFilters.from || theaterAppliedFilters.to
        );
    }, [theaterAppliedFilters.title, theaterAppliedFilters.from, theaterAppliedFilters.to]);

    useEffect(() => {
        if (theaterActiveFolderId === null) return;
        if (!folderMap.has(theaterActiveFolderId)) {
            setTheaterActiveFolderId(null);
        }
    }, [theaterActiveFolderId, folderMap]);

    const handleReaction = async (messageId: number, emoji: string) => {
        setReactionPickerFor(null);
        try {
            const res = await reactToChatMessage(group.group_id, messageId, emoji);
            const data = res.data;
            if (data?.reactions) {
                setMessages((prev) => prev.map((msg) => (
                    msg.message_id === messageId ? { ...msg, reactions: data.reactions } : msg
                )));
            }
        } catch (err) {
            console.error('Failed to react to message', err);
        }
    };

    const groupReactions = (reactions: any[] = []) => {
        return reactions.map((reaction) => reaction.emoji);
    };

    const handleLoadVideo = (messageId: number) => {
        setLoadedVideos((prev) => new Set(prev).add(messageId));
    };

    const handleLoadTheaterVideo = (itemId: number) => {
        setLoadedTheaterVideos((prev) => new Set(prev).add(itemId));
    };

    const getTheaterImageUrl = (item: any) => {
        const mediaUrl = String(item?.media_url || item?.fallback_url || '');
        if (!mediaUrl) return null;
        if (item?.item_type === 'image' || /\.(png|jpe?g|webp|gif)$/i.test(mediaUrl)) {
            return mediaUrl;
        }
        return null;
    };

    const handleDownloadMessageImage = async (message: any, format: DownloadImageFormat) => {
        const sourceUrl = String(message?.attachment_url || '');
        if (!sourceUrl) return;
        try {
            await downloadImageUrlAs(
                sourceUrl,
                `maison-salon-image-${message?.message_id || Date.now()}`,
                format
            );
        } catch (err) {
            console.error('Failed to download salon image', err);
            alert('画像の保存に失敗しました。しばらくしてからお試しください。');
        }
    };

    const handleDownloadMessageNote = async (message: any, format: DownloadImageFormat) => {
        const text = String(message?.content || '').trim();
        if (!text) return;
        try {
            await downloadTextCardAsImage({
                title: resolvedGroup?.group_name || 'Salon note',
                meta: `${resolveName(message?.display_name)} / ${formatChatTimestamp(message?.created_at)}`,
                text,
                filenameBase: `maison-salon-note-${message?.message_id || Date.now()}`,
                format,
            });
        } catch (err) {
            console.error('Failed to download salon note', err);
            alert('ノートの保存に失敗しました。しばらくしてからお試しください。');
        }
    };

    const handleDownloadTheaterImage = async (item: any, format: DownloadImageFormat) => {
        const sourceUrl = getTheaterImageUrl(item);
        if (!sourceUrl) return;
        try {
            await downloadImageUrlAs(
                sourceUrl,
                `maison-salon-theater-image-${item?.item_id || Date.now()}`,
                format
            );
        } catch (err) {
            console.error('Failed to download theater image', err);
            alert('画像の保存に失敗しました。しばらくしてからお試しください。');
        }
    };

    const handleDownloadTheaterNote = async (item: any, format: DownloadImageFormat) => {
        const text = String(item?.content_text || '').trim();
        if (!text) return;
        const metaParts = [
            item?.created_by_name || 'Unknown',
            item?.created_at ? new Date(item.created_at).toLocaleString() : '',
            item?.source_user_name ? `元投稿: ${item.source_user_name}` : '',
        ].filter(Boolean);
        try {
            await downloadTextCardAsImage({
                title: item?.title || (item?.item_type === 'note' ? 'Salon note' : 'Salon text'),
                meta: metaParts.join(' / '),
                text,
                filenameBase: `maison-salon-note-${item?.item_id || Date.now()}`,
                format,
            });
        } catch (err) {
            console.error('Failed to download theater note', err);
            alert('ノートの保存に失敗しました。しばらくしてからお試しください。');
        }
    };

    const renderTheaterMedia = (item: any) => {
        if (!item.media_url && !item.hls_url && !item.fallback_url) return null;
        const lower = String(item.media_url || item.fallback_url || item.hls_url || '').toLowerCase();
        const imageUrl = getTheaterImageUrl(item);
        const isImage = Boolean(imageUrl);
        const isAudio = item.item_type === 'voice' || /\.(mp3|wav|m4a|aac|ogg)$/.test(lower);
        const isVideo = item.item_type === 'movie' || /\.(mp4|webm|mov)$/.test(lower);
        if (isImage) {
            return <img src={imageUrl || item.media_url} alt="theater" className="max-h-64 rounded border" loading="lazy" />;
        }
        if (isAudio) {
            return <audio src={item.media_url} controls />;
        }
        if (isVideo) {
            const isLoaded = loadedTheaterVideos.has(item.item_id);
            if (!isLoaded) {
                return (
                    <button
                        className="relative w-full max-w-md rounded border overflow-hidden"
                        onClick={() => handleLoadTheaterVideo(item.item_id)}
                    >
                        {item.thumb_url ? (
                            <img src={item.thumb_url} alt="thumbnail" className="w-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-40 bg-gray-100 text-gray-500 text-sm">
                                プレビュー
                            </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center text-white text-sm font-semibold">
                            ▶︎ 再生
                        </div>
                    </button>
                );
            }
            return (
                <HlsVideoPlayer
                    hlsSrc={item.hls_url || null}
                    fallbackSrc={item.fallback_url || item.media_url || null}
                    className="max-h-64 rounded border"
                />
            );
        }
        return null;
    };

    const renderTheaterItemCard = (item: any, showFolderPath: boolean) => {
        const canDelete = isStaff || (isFamiglia && item.created_by === currentUser.user_id);
        const theaterImageUrl = getTheaterImageUrl(item);
        const hasTextDownload = Boolean(String(item.content_text || '').trim());
        return (
        <div key={item.item_id} className="rounded-lg border bg-white p-4 space-y-2">
            <div className="text-xs text-gray-500">
                {item.created_by_name || 'Unknown'} ・ {new Date(item.created_at).toLocaleString()}
            </div>
            {showFolderPath && (
                <div className="text-xs text-gray-500">フォルダ: {getFolderPath(item.folder_id)}</div>
            )}
            {item.item_type === 'note' && item.source_user_name && (
                <div className="text-xs text-gray-500">元の投稿者: {item.source_user_name}</div>
            )}
            {item.title && <div className="text-sm font-semibold text-gray-800">{item.title}</div>}
            {item.content_text && <div className="text-sm whitespace-pre-wrap">{item.content_text}</div>}
            {renderTheaterMedia(item)}
            {(theaterImageUrl || hasTextDownload) && (
                <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
                    {theaterImageUrl && DOWNLOAD_FORMATS.map((format) => (
                        <button
                            key={`image-${item.item_id}-${format}`}
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-2 py-1 text-gray-600 hover:text-gray-900"
                            onClick={() => handleDownloadTheaterImage(item, format)}
                        >
                            <Download size={13} />
                            画像{format.toUpperCase()}保存
                        </button>
                    ))}
                    {hasTextDownload && DOWNLOAD_FORMATS.map((format) => (
                        <button
                            key={`note-${item.item_id}-${format}`}
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-2 py-1 text-gray-600 hover:text-gray-900"
                            onClick={() => handleDownloadTheaterNote(item, format)}
                        >
                            <Download size={13} />
                            ノート{format.toUpperCase()}保存
                        </button>
                    ))}
                </div>
            )}
            {(isStaff || canDelete) && (
                <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
                    {isStaff && (
                        <select
                            className="border rounded px-2 py-1"
                            value={item.folder_id ? `id:${item.folder_id}` : 'none'}
                            onChange={(e) => handleMoveItem(item.item_id, e.target.value)}
                        >
                            <option value="none">未分類へ移動</option>
                            {folderOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label} へ移動
                                </option>
                            ))}
                        </select>
                    )}
                    {canDelete && (
                        <button
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteItem(item.item_id)}
                        >
                            削除
                        </button>
                    )}
                </div>
            )}
        </div>
        );
    };

    return (
        <div
            className="flex-1 flex flex-col h-full min-h-0 relative"
            style={{
                backgroundColor: withAlpha(chatTheme.colors.background, darkTheme ? 0.96 : 0.9),
                color: chatTheme.colors.text,
            }}
        >
            <div className="absolute left-3 top-1/2 z-30 -translate-y-1/2 flex flex-col gap-2">
                <button
                    type="button"
                    className="h-9 w-9 rounded-full border shadow-md"
                    onClick={scrollToTop}
                    aria-label="ページ最上部へ"
                    title="ページ最上部へ"
                    style={{
                        borderColor: chatTheme.colors.border,
                        backgroundColor: withAlpha(chatTheme.colors.card, 0.96),
                        color: chatTheme.colors.text,
                    }}
                >
                    <ChevronUp className="mx-auto" size={18} />
                </button>
                <button
                    type="button"
                    className="h-9 w-9 rounded-full border shadow-md"
                    onClick={scrollToEnd}
                    aria-label="ページ最下部へ"
                    title="ページ最下部へ"
                    style={{
                        borderColor: chatTheme.colors.border,
                        backgroundColor: withAlpha(chatTheme.colors.card, 0.96),
                        color: chatTheme.colors.text,
                    }}
                >
                    <ChevronDown className="mx-auto" size={18} />
                </button>
            </div>
            <div
                className="p-3 md:p-4 border-b flex flex-wrap items-center gap-2 md:gap-3"
                style={{
                    ...panelStyle,
                    borderColor: withAlpha(chatTheme.colors.border, 0.7),
                }}
            >
                {showBackButton && onBackToList && (
                    <button
                        className="md:hidden px-2 py-1 text-xs md:text-sm border rounded whitespace-nowrap"
                        onClick={onBackToList}
                        style={{
                            color: chatTheme.colors.primary,
                            borderColor: chatTheme.colors.primary,
                            backgroundColor: withAlpha(chatTheme.colors.primary, darkTheme ? 0.16 : 0.08),
                        }}
                    >
                        ← 戻る
                    </button>
                )}
                {isDirectGroup && directPeerUserId ? (
                    <button
                        type="button"
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center overflow-hidden shrink-0 hover:ring-2 hover:ring-yellow-400"
                        style={{
                            backgroundColor: softSurfaceColor,
                            color: mutedTextColor,
                        }}
                        onClick={(event) => handleResidentProfileNavigate(directPeerUserId, event)}
                        aria-label={`${resolvedGroup.group_name} のプロフィールへ`}
                        title="住人プロフィールを見る"
                    >
                        {resolvedGroup.icon_path ? (
                            <img src={resolvedGroup.icon_path} alt={resolvedGroup.group_name || 'Resident icon'} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-xs md:text-sm">💬</span>
                        )}
                    </button>
                ) : (
                    <div
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center overflow-hidden shrink-0"
                        style={{
                            backgroundColor: softSurfaceColor,
                            color: mutedTextColor,
                        }}
                    >
                        {resolvedGroup.icon_path ? (
                            <img src={resolvedGroup.icon_path} alt="Group icon" className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-xs md:text-sm">💬</span>
                        )}
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    {isDirectGroup && directPeerUserId ? (
                        <button
                            type="button"
                            className="font-bold text-sm md:text-lg line-clamp-1 text-left hover:underline"
                            onClick={(event) => handleResidentProfileNavigate(directPeerUserId, event)}
                        >
                            {resolvedGroup.group_name}
                        </button>
                    ) : (
                        <h2 className="font-bold text-sm md:text-lg line-clamp-1">{resolvedGroup.group_name}</h2>
                    )}
                    <div className="text-[11px]" style={{ color: mutedTextColor }}>
                        {isDirectGroup ? '個人チャット' : isAnnouncementGroup ? 'アナウンスチャット' : 'グループサロン'}
                    </div>
                </div>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs md:text-sm shrink-0">
                    <button
                        className="px-2.5 md:px-3 py-1 rounded-full border flex items-center gap-1"
                        onClick={() => {
                            setSearchOpen((prev) => !prev);
                            if (!searchOpen) {
                                setTimeout(() => searchInputRef.current?.focus(), 0);
                            }
                        }}
                        style={{
                            color: searchOpen ? chatTheme.colors.primary : chatTheme.colors.text,
                            borderColor: searchOpen ? chatTheme.colors.primary : chatTheme.colors.border,
                            backgroundColor: searchOpen ? withAlpha(chatTheme.colors.primary, darkTheme ? 0.16 : 0.08) : 'transparent',
                        }}
                    >
                        <Search size={14} />
                        検索
                    </button>
                    <button
                        className="px-2.5 md:px-3 py-1 rounded-full border flex items-center gap-1"
                        onClick={toggleGroupPin}
                        style={{
                            color: isPinned ? chatTheme.colors.primary : chatTheme.colors.text,
                            borderColor: isPinned ? chatTheme.colors.primary : chatTheme.colors.border,
                            backgroundColor: isPinned ? withAlpha(chatTheme.colors.primary, darkTheme ? 0.16 : 0.08) : 'transparent',
                        }}
                    >
                        <Pin size={14} className={isPinned ? 'fill-current' : ''} />
                        {isPinned ? '固定中' : 'ピン'}
                    </button>
                    <button
                        className="px-2.5 md:px-3 py-1 rounded-full border flex items-center gap-1"
                        onClick={() => setMuteOpen(true)}
                        style={{
                            color: muteAll || muteNight || mutedUserIds.size > 0 ? chatTheme.colors.primary : chatTheme.colors.text,
                            borderColor: muteAll || muteNight || mutedUserIds.size > 0 ? chatTheme.colors.primary : chatTheme.colors.border,
                            backgroundColor: muteAll || muteNight || mutedUserIds.size > 0
                                ? withAlpha(chatTheme.colors.primary, darkTheme ? 0.16 : 0.08)
                                : 'transparent',
                        }}
                    >
                        ミュート
                    </button>
                    <button
                        className="px-2.5 md:px-3 py-1 rounded-full border flex items-center gap-1"
                        onClick={handleRefreshChat}
                        disabled={refreshingChat}
                        style={{
                            color: refreshingChat ? mutedTextColor : chatTheme.colors.text,
                            borderColor: chatTheme.colors.border,
                            backgroundColor: 'transparent',
                        }}
                    >
                        <RefreshCw size={14} className={refreshingChat ? 'animate-spin' : ''} />
                        更新
                    </button>
                    <button
                        className="px-2.5 md:px-3 py-1 rounded-full border"
                        onClick={() => setViewMode('chat')}
                        style={{
                            backgroundColor: viewMode === 'chat' ? chatTheme.colors.primary : 'transparent',
                            borderColor: viewMode === 'chat' ? chatTheme.colors.primary : chatTheme.colors.border,
                            color: viewMode === 'chat' ? primaryTextColor : mutedTextColor,
                        }}
                    >
                        チャット
                    </button>
                    {canUseTheater && (
                        <button
                            className="px-2.5 md:px-3 py-1 rounded-full border"
                            onClick={() => setViewMode('theater')}
                            style={{
                                backgroundColor: viewMode === 'theater' ? chatTheme.colors.primary : 'transparent',
                                borderColor: viewMode === 'theater' ? chatTheme.colors.primary : chatTheme.colors.border,
                                color: viewMode === 'theater' ? primaryTextColor : mutedTextColor,
                            }}
                        >
                            シアター
                        </button>
                    )}
                </div>
            </div>

            {searchOpen && (
                <div
                    className="border-b px-3 py-2"
                    style={{
                        ...panelStyle,
                        borderColor: withAlpha(chatTheme.colors.border, 0.7),
                    }}
                >
                    <div className="flex items-center gap-2">
                        <div
                            className="flex-1 flex items-center gap-2 border rounded-full px-3 py-1"
                            style={{
                                borderColor: chatTheme.colors.border,
                                backgroundColor: inputSurface,
                            }}
                        >
                            <Search size={14} style={{ color: mutedTextColor }} />
                            <input
                                ref={searchInputRef}
                                className="flex-1 text-sm focus:outline-none"
                                placeholder="かな/カナ・自然文で検索"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ backgroundColor: 'transparent', color: chatTheme.colors.text }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            {searchQuery && (
                                <button
                                    className="text-xs"
                                    style={{ color: mutedTextColor }}
                                    onClick={() => setSearchQuery('')}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        <button
                            className="text-xs"
                            style={{ color: mutedTextColor }}
                            onClick={() => {
                                setSearchOpen(false);
                                setSearchQuery('');
                                setSearchResults([]);
                                setSearchError('');
                            }}
                        >
                            閉じる
                        </button>
                    </div>
                    <div className="mt-2 text-xs flex items-center gap-2" style={{ color: mutedTextColor }}>
                        {searchLoading && (
                            <>
                                <span
                                    className="h-3 w-3 animate-spin rounded-full border-2 border-t-transparent"
                                    style={{ borderColor: mutedTextColor, borderTopColor: 'transparent' }}
                                />
                                検索中...
                            </>
                        )}
                        {!searchLoading && searchError && <span className="text-red-500">{searchError}</span>}
                        {!searchLoading && !searchError && searchQuery.trim() && (
                            <span>一致: {searchResults.length} 件</span>
                        )}
                        {!searchLoading && !searchError && !searchQuery.trim() && (
                            <span>ひらがな・カタカナの揺れや自然文でも検索できます。</span>
                        )}
                    </div>
                    {searchQuery.trim() && searchResults.length > 0 && (
                        <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
                            {searchResults.slice(0, 20).map((item) => (
                                <button
                                    key={item.message_id}
                                    className="w-full text-left rounded-lg border px-3 py-2"
                                    onClick={() => {
                                        jumpToMessage(item.message_id);
                                    }}
                                    style={{
                                        borderColor: withAlpha(chatTheme.colors.border, 0.7),
                                        backgroundColor: withAlpha(chatTheme.colors.card, darkTheme ? 0.74 : 0.58),
                                    }}
                                >
                                    <div className="text-xs mb-1" style={{ color: mutedTextColor }}>
                                        {item.sender_display_name || '住人'} ・ {formatChatTimestamp(item.created_at)}
                                    </div>
                                    <div className="text-sm line-clamp-2" style={{ color: chatTheme.colors.text }}>
                                        {highlightText(item.content)}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'chat' ? (
                <div
                    className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain"
                    ref={scrollRef}
                    onScroll={handleScroll}
                    style={baseScrollStyle}
                >
                    <div
                        className="p-3 md:p-4 space-y-4 relative"
                        style={{
                            backgroundColor: resolvedGroup.background_path
                                ? withAlpha(chatTheme.colors.background, darkTheme ? 0.9 : 0.82)
                                : withAlpha(chatTheme.colors.background, darkTheme ? 0.96 : 0.78),
                            ...(resolvedGroup.background_path
                                ? {
                                      backgroundImage: `url(${resolvedGroup.background_path})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundBlendMode: 'overlay',
                                  }
                                : {}),
                        }}
                    >
                        <div ref={topSentinelRef} className="h-1" aria-hidden="true" />
                        <div className="flex justify-center">
                            {loadingOlder && (
                                <div
                                    className="mt-1 flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] shadow-sm"
                                    style={{
                                        borderColor: chatTheme.colors.border,
                                        backgroundColor: withAlpha(chatTheme.colors.card, 0.92),
                                        color: mutedTextColor,
                                    }}
                                >
                                    <span
                                        className="h-3 w-3 animate-spin rounded-full border-2 border-t-transparent"
                                        style={{ borderColor: mutedTextColor, borderTopColor: 'transparent' }}
                                    />
                                    過去ログを読み込み中...
                                </div>
                            )}
                            {!loadingOlder && !hasMoreMessages && messages.length > 0 && (
                                <div
                                    className="mt-1 rounded-full border px-3 py-1 text-[11px] shadow-sm"
                                    style={{
                                        borderColor: chatTheme.colors.border,
                                        backgroundColor: withAlpha(chatTheme.colors.card, 0.92),
                                        color: mutedTextColor,
                                    }}
                                >
                                    これ以上過去のメッセージはありません。
                                </div>
                            )}
                        </div>
                        <div ref={contentRef} className="relative z-10 space-y-4">
                            {messages.map((msg: any) => {
                                const isMe = msg.user_id === currentUser.user_id;
                                const emojiLikes = groupReactions(msg.reactions || []);
                                const emojiPreview = emojiLikes.slice(0, MAX_EMOJI_PREVIEW);
                                const hasMoreEmojis = emojiLikes.length > MAX_EMOJI_PREVIEW;
                                const hasAttachment = !msg.is_deleted && Boolean(
                                    msg.attachment_type
                                    && (msg.attachment_url || msg.attachment_hls_url || msg.attachment_fallback_url || msg.attachment_expired)
                                );
                                const createdAt = parseCreatedAt(msg.created_at);
                                const cancelWindowMs = 30_000;
                                const canCancel = isMe && !msg.is_deleted && Date.now() - createdAt <= cancelWindowMs;
                                const mentionLabels: string[] = msg.mention_all
                                    ? ['@All']
                                    : Array.isArray(msg.mentions)
                                        ? Array.from(new Set<string>(msg.mentions.map((id: number) => resolveMentionLabel(id))))
                                        : [];
                                const bubblePalette = getMessagePalette(msg.role_type, msg.floor_level, isMe);
                                const canDownloadAttachmentImage = Boolean(
                                    !msg.is_deleted
                                    && !msg.attachment_expired
                                    && msg.attachment_url
                                    && (msg.attachment_type === 'image' || msg.attachment_type === 'stamp')
                                );
                                return (
                                    <div
                                        key={msg.message_id}
                                        ref={(el) => { messageRefs.current[msg.message_id] = el; }}
                                        className={`flex gap-2 items-end ${isMe ? 'flex-row-reverse' : 'flex-row'}`}
                                    >
                                        <Avatar
                                            username={resolveName(msg.display_name)}
                                            src={msg.avatar_path}
                                            size="sm"
                                            theme={getTheme(msg.role_type, msg.floor_level)}
                                            className="shrink-0"
                                            onClick={() => handleResidentProfileNavigate(msg.user_id)}
                                        />
                                        <div className="min-w-0 max-w-[75%] md:max-w-md">
                                            <div
                                                className={`text-[11px] md:text-xs mb-1 flex items-center gap-2 ${
                                                    isMe ? 'justify-end' : ''
                                                }`}
                                                style={{ color: bubblePalette.secondaryTextColor }}
                                            >
                                                <button
                                                    type="button"
                                                    className={`hover:underline ${isMe ? 'cursor-default' : 'cursor-pointer'}`}
                                                    onClick={() => mentionUserFromMessage(msg.user_id)}
                                                    disabled={isMe}
                                                >
                                                    {resolveName(msg.display_name)}
                                                </button>
                                                <span>{formatChatTimestamp(msg.created_at)}</span>
                                            </div>
                                            <div
                                                className={`p-2.5 md:p-3 rounded-lg cursor-pointer border ${
                                                    isMe ? 'rounded-br-none' : 'rounded-bl-none'
                                                } ${highlightMessageId === msg.message_id ? 'ring-2' : ''}`}
                                                style={{
                                                    backgroundColor: bubblePalette.backgroundColor,
                                                    borderColor: bubblePalette.borderColor,
                                                    color: bubblePalette.textColor,
                                                    ...(highlightMessageId === msg.message_id ? { '--tw-ring-color': chatTheme.colors.primary } : {}),
                                                } as any}
                                                onClick={() => {
                                                    setActiveMessageId((prev) => (prev === msg.message_id ? null : msg.message_id));
                                                    setReactionPickerFor(null);
                                                }}
                                            >
                                                {msg.reply_to_message_id && (
                                                    <button
                                                        type="button"
                                                        className="mb-2 text-left text-[11px] md:text-xs underline-offset-2 hover:underline"
                                                        style={{ color: bubblePalette.secondaryTextColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            jumpToMessage(msg.reply_to_message_id);
                                                        }}
                                                    >
                                                        ↩︎ {resolveName(msg.reply_to_display_name)}: {msg.reply_to_content}
                                                    </button>
                                                )}
                                                {mentionLabels.length > 0 && (
                                                    <div className="mb-2 flex flex-wrap items-center gap-1 text-[10px] md:text-[11px]">
                                                        <span style={{ color: bubblePalette.secondaryTextColor }}>
                                                            {t('メンション')}:
                                                        </span>
                                                        {mentionLabels.map((label: string) => (
                                                            <span
                                                                key={`${msg.message_id}-${label}`}
                                                                className="rounded-full px-2 py-0.5"
                                                                style={{
                                                                    backgroundColor: bubblePalette.chipBackground,
                                                                    color: bubblePalette.chipTextColor,
                                                                }}
                                                            >
                                                                {label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {hasAttachment && (
                                                    <div className="mb-2">
                                                        {msg.attachment_expired ? (
                                                            <div className="text-[11px] md:text-xs" style={{ color: bubblePalette.secondaryTextColor }}>
                                                                有効期限が切れました。
                                                            </div>
                                                ) : msg.attachment_type === 'stamp' ? (
                                                    <img
                                                        src={msg.attachment_url}
                                                        alt="stamp"
                                                        className="w-28 h-28 object-contain"
                                                        loading="lazy"
                                                    />
                                                ) : msg.attachment_type === 'image' ? (
                                                    <img
                                                        src={msg.attachment_url}
                                                        alt="attachment"
                                                        className="max-w-full rounded-lg border"
                                                        loading="lazy"
                                                    />
                                                ) : msg.attachment_type === 'audio' ? (
                                                    <audio src={msg.attachment_url} controls className="w-full max-w-full" />
                                                ) : (
                                                    (() => {
                                                        const isLoaded = loadedVideos.has(msg.message_id);
                                                        if (!isLoaded) {
                                                            return (
                                                                <button
                                                                    className="relative w-full max-w-full rounded-lg border overflow-hidden"
                                                                    onClick={() => handleLoadVideo(msg.message_id)}
                                                                >
                                                                    {msg.attachment_thumb_url ? (
                                                                        <img
                                                                            src={msg.attachment_thumb_url}
                                                                            alt="thumbnail"
                                                                            className="w-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="flex items-center justify-center h-32 bg-gray-100 text-gray-500 text-xs">
                                                                            プレビュー
                                                                        </div>
                                                                    )}
                                                                    <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold">
                                                                        ▶︎ 再生
                                                                    </div>
                                                                </button>
                                                            );
                                                        }
                                                        return (
                                                            <HlsVideoPlayer
                                                                hlsSrc={msg.attachment_hls_url || null}
                                                                fallbackSrc={msg.attachment_fallback_url || msg.attachment_url || null}
                                                                className="max-w-full rounded-lg border"
                                                            />
                                                        );
                                                    })()
                                                )}
                                                {canDownloadAttachmentImage && (
                                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                                        {DOWNLOAD_FORMATS.map((format) => (
                                                            <button
                                                                key={`${msg.message_id}-${format}`}
                                                                type="button"
                                                                className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] md:text-[11px]"
                                                                style={{
                                                                    color: bubblePalette.secondaryTextColor,
                                                                    borderColor: withAlpha(bubblePalette.secondaryTextColor, 0.35),
                                                                    backgroundColor: bubblePalette.chipBackground,
                                                                }}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleDownloadMessageImage(msg, format);
                                                                }}
                                                            >
                                                                <Download size={12} />
                                                                {format.toUpperCase()}保存
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                                {msg.content && (
                                                    <div
                                                        className={`text-[13px] md:text-sm leading-relaxed whitespace-pre-wrap break-words ${msg.is_deleted ? 'italic' : ''}`}
                                                        style={{ color: msg.is_deleted ? bubblePalette.secondaryTextColor : bubblePalette.textColor }}
                                                    >
                                                        {searchQuery.trim() ? highlightText(msg.content) : msg.content}
                                                    </div>
                                                )}
                                            </div>
                                            {activeMessageId === msg.message_id && (
                                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                    <button
                                                        className="flex items-center gap-1"
                                                        style={{ color: mutedTextColor }}
                                                        onClick={() => {
                                                            if (canEmoji) {
                                                                setReactionPickerFor(msg.message_id);
                                                            } else {
                                                                handleReaction(msg.message_id, '👍');
                                                            }
                                                        }}
                                                    >
                                                        <ThumbsUp size={14} />
                                                        {t('いいね')}
                                                    </button>
                                                    {canPostChat && (
                                                        <button
                                                            className="flex items-center gap-1"
                                                            style={{ color: mutedTextColor }}
                                                            onClick={() => {
                                                                setReplyTo(msg);
                                                                setActiveMessageId(null);
                                                            }}
                                                        >
                                                            <MessageSquare size={14} />
                                                            {t('リプライ')}
                                                        </button>
                                                    )}
                                                    {msg.content && !msg.is_deleted && (
                                                        <button
                                                            className="flex items-center gap-1"
                                                            style={{ color: mutedTextColor }}
                                                            onClick={() => {
                                                                setTranslateSource(msg.content || '');
                                                                setTranslateOpen(true);
                                                                setActiveMessageId(null);
                                                            }}
                                                        >
                                                            <Languages size={14} />
                                                            {t('翻訳')}
                                                        </button>
                                                    )}
                                                    {msg.content && !msg.is_deleted && DOWNLOAD_FORMATS.map((format) => (
                                                        <button
                                                            key={`note-${msg.message_id}-${format}`}
                                                            className="flex items-center gap-1"
                                                            style={{ color: mutedTextColor }}
                                                            onClick={() => {
                                                                handleDownloadMessageNote(msg, format);
                                                                setActiveMessageId(null);
                                                            }}
                                                        >
                                                            <Download size={14} />
                                                            ノート{format.toUpperCase()}保存
                                                        </button>
                                                    ))}
                                                    {canCancel && (
                                                        <button
                                                            className="flex items-center gap-1"
                                                            style={{ color: mutedTextColor }}
                                                            onClick={() => {
                                                                if (!confirm('送信を取り消しますか？')) {
                                                                    setActiveMessageId(null);
                                                                    return;
                                                                }
                                                                cancelChatMessage(group.group_id, msg.message_id)
                                                                    .then(() => {
                                                                        setMessages((prev) =>
                                                                            prev.map((m) =>
                                                                                m.message_id === msg.message_id
                                                                                    ? {
                                                                                          ...m,
                                                                                          is_deleted: true,
                                                                                          content: '送信を取り消しました',
                                                                                          attachment_url: null,
                                                                                          attachment_thumb_url: null,
                                                                                          attachment_hls_url: null,
                                                                                          attachment_fallback_url: null,
                                                                                      }
                                                                                    : m
                                                                            )
                                                                        );
                                                                    })
                                                                    .catch(() => alert('取り消しに失敗しました（30秒を過ぎている可能性があります）'));
                                                                setActiveMessageId(null);
                                                            }}
                                                        >
                                                            送信取消
                                                        </button>
                                                    )}
                                                    {isStaff && canUseTheater && (
                                                        <button
                                                            className="flex items-center gap-1"
                                                            style={{ color: mutedTextColor }}
                                                            onClick={() => {
                                                                handleNote(msg.message_id);
                                                                setActiveMessageId(null);
                                                            }}
                                                        >
                                                            <Bookmark size={14} />
                                                            ノート
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {reactionPickerFor === msg.message_id && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {EMOJI_CHOICES.map((emoji) => (
                                                        <button
                                                            key={emoji}
                                                            className="text-lg"
                                                            onClick={() => handleReaction(msg.message_id, emoji)}
                                                        >
                                                            {emoji}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {emojiPreview.length > 0 && (
                                                <button
                                                    className="mt-2 flex items-center"
                                                    onClick={() => setReactionModal({ messageId: msg.message_id, reactions: msg.reactions || [] })}
                                                >
                                                    {emojiPreview.map((emoji, index) => (
                                                        <span
                                                            key={`${msg.message_id}-${emoji}-${index}`}
                                                            className={`text-base ${index > 0 ? '-ml-2' : ''}`}
                                                        >
                                                            {emoji}
                                                        </span>
                                                    ))}
                                                    {hasMoreEmojis && <span className="text-xs -ml-2">…</span>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {canPostChat ? (
                        <div
                            className="p-3 md:p-4 border-t space-y-2 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sticky bottom-0 z-20"
                            style={{
                                ...panelStyle,
                                borderColor: withAlpha(chatTheme.colors.border, 0.7),
                            }}
                        >
                            {noteStatus && <div className="text-xs" style={{ color: chatTheme.colors.primary }}>{noteStatus}</div>}
                            {sendError && <div className="text-xs text-red-600">{sendError}</div>}
                            {uploadError && <div className="text-xs text-red-600">{uploadError}</div>}
                            {(mentionAll || selectedMentionUsers.length > 0) && (
                                <div className="flex flex-wrap gap-2">
                                    {mentionAll && (
                                        <span
                                            className="text-xs px-2 py-1 rounded-full"
                                            style={{ backgroundColor: withAlpha(chatTheme.colors.primary, 0.12), color: chatTheme.colors.primary }}
                                        >
                                            @All
                                        </span>
                                    )}
                                    {selectedMentionUsers.map((user) => (
                                        <span
                                            key={user.user_id}
                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                                            style={{ backgroundColor: withAlpha(chatTheme.colors.primary, 0.12), color: chatTheme.colors.primary }}
                                        >
                                            @{resolveName(user.display_name)}
                                            <button
                                                type="button"
                                                className="text-[10px] leading-none"
                                                style={{ color: withAlpha(chatTheme.colors.primary, 0.7) }}
                                                onClick={() => toggleMentionUser(user.user_id)}
                                                aria-label="メンション解除"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            {replyTo && (
                                <div
                                    className="flex items-center justify-between border rounded px-3 py-2 text-xs"
                                    style={{
                                        backgroundColor: softSurfaceColor,
                                        borderColor: chatTheme.colors.border,
                                        color: chatTheme.colors.text,
                                    }}
                                >
                                    <div>
                                        返信先: {resolveName(replyTo.display_name)} / {replyTo.content}
                                    </div>
                                    <button onClick={() => setReplyTo(null)}>
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                            {attachment && (
                                <div
                                    className="flex items-center gap-3 border rounded p-2 text-xs"
                                    style={{
                                        backgroundColor: softSurfaceColor,
                                        borderColor: chatTheme.colors.border,
                                        color: chatTheme.colors.text,
                                    }}
                                >
                                    {attachment.type === 'stamp' ? (
                                        <img src={attachment.url} alt="stamp" className="w-16 h-16 object-contain" />
                                    ) : attachment.type === 'image' ? (
                                        <img src={attachment.url} alt="preview" className="w-16 h-16 object-cover rounded" />
                                    ) : attachment.type === 'audio' ? (
                                        <audio src={attachment.url} controls className="h-8" />
                                    ) : attachment.thumbUrl ? (
                                        <img src={attachment.thumbUrl} alt="preview" className="w-20 h-16 object-cover rounded" />
                                    ) : (
                                        <video src={attachment.url} className="w-20 h-16 rounded" preload="metadata" />
                                    )}
                                    <button className="underline" style={{ color: mutedTextColor }} onClick={() => setAttachment(null)}>
                                        添付を削除
                                    </button>
                                </div>
                            )}
                            {uploading && (
                                <div className="space-y-1">
                                    <div className="text-sm font-semibold" style={{ color: chatTheme.colors.primary }}>
                                        {uploadProgress !== null && uploadProgress < 100
                                            ? `Uploading... ${uploadProgress}%`
                                            : '変換中...'}
                                    </div>
                                    {uploadProgress !== null && (
                                        <div
                                            className="h-1.5 w-full rounded-full overflow-hidden"
                                            style={{ backgroundColor: withAlpha(chatTheme.colors.primary, 0.12) }}
                                        >
                                            <div
                                                className="h-full"
                                                style={{ backgroundColor: chatTheme.colors.primary, width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2 flex-wrap items-center">
                                <button
                                    className="p-2 border rounded-full"
                                    onClick={() => setMentionOpen(true)}
                                    style={{ color: mutedTextColor, borderColor: chatTheme.colors.border }}
                                >
                                    <AtSign size={18} />
                                </button>
                                <button
                                    className="p-2 border rounded-full"
                                    onClick={() => imageInputRef.current?.click()}
                                    style={{ color: mutedTextColor, borderColor: chatTheme.colors.border }}
                                >
                                    <Image size={18} />
                                </button>
                                <button
                                    className="p-2 border rounded-full"
                                    onClick={() => videoInputRef.current?.click()}
                                    style={{ color: mutedTextColor, borderColor: chatTheme.colors.border }}
                                >
                                    <Video size={18} />
                                </button>
                                <button
                                    className="p-2 border rounded-full"
                                    onClick={() => audioInputRef.current?.click()}
                                    style={{ color: mutedTextColor, borderColor: chatTheme.colors.border }}
                                >
                                    <Music size={18} />
                                </button>
                                <button
                                    className="p-2 border rounded-full"
                                    onClick={() => {
                                        stampAutoOpenRef.current = false;
                                        setStampPage(0);
                                        setStampTab('recent');
                                        if (!stampOpen) {
                                            setStampSuggestionsExpanded(false);
                                            setStampListExpanded(false);
                                        }
                                        setStampOpen((prev) => !prev);
                                    }}
                                    style={{
                                        color: stampOpen ? chatTheme.colors.primary : mutedTextColor,
                                        borderColor: stampOpen ? chatTheme.colors.primary : chatTheme.colors.border,
                                        backgroundColor: stampOpen ? withAlpha(chatTheme.colors.primary, 0.12) : 'transparent',
                                    }}
                                >
                                    <Smile size={18} />
                                </button>
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            handleAttachmentUpload(file);
                                        }
                                        e.currentTarget.value = '';
                                    }}
                                />
                                <input
                                    ref={videoInputRef}
                                    type="file"
                                    accept="video/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            handleAttachmentUpload(file);
                                        }
                                        e.currentTarget.value = '';
                                    }}
                                />
                                <input
                                    ref={audioInputRef}
                                    type="file"
                                    accept="audio/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            handleAttachmentUpload(file);
                                        }
                                        e.currentTarget.value = '';
                                    }}
                                />
                                <textarea
                                    ref={chatInputRef}
                                    className="flex-1 min-w-[10rem] max-w-[75%] md:max-w-md p-2 border rounded-xl px-4 text-[16px] md:text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none overflow-y-auto"
                                    placeholder={t('メッセージを入力...')}
                                    value={input}
                                    style={{
                                        borderColor: chatTheme.colors.border,
                                        backgroundColor: inputSurface,
                                        color: chatTheme.colors.text,
                                    }}
                                    rows={2}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setInput(next);
                                        if (!mentionOpen && canPostChat) {
                                            const endsWithAt = next.endsWith('@') || next.endsWith('＠');
                                            const prevEndsWithAt = input.endsWith('@') || input.endsWith('＠');
                                            if (endsWithAt && !prevEndsWithAt) {
                                                setMentionOpen(true);
                                            }
                                        }
                                    }}
                                    onPaste={(e) => {
                                        const clipboard = e.clipboardData;
                                        if (!clipboard) return;
                                        const files = Array.from(clipboard.files || []);
                                        let imageFile = files.find((file) => file.type.startsWith('image/')) || null;
                                        if (!imageFile) {
                                            const items = Array.from(clipboard.items || []);
                                            const imageItem = items.find(
                                                (item) => item.kind === 'file' && item.type.startsWith('image/')
                                            );
                                            if (imageItem) {
                                                imageFile = imageItem.getAsFile();
                                            }
                                        }
                                        if (imageFile) {
                                            e.preventDefault();
                                            handleAttachmentUpload(imageFile);
                                        }
                                    }}
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellCheck={false}
                                    onInput={(e) => autoResizeTextarea(e.currentTarget, chatInputMaxHeight)}
                                    onFocus={(e) => autoResizeTextarea(e.currentTarget, chatInputMaxHeight)}
                                    onKeyDown={(e) => {
                                        if (!mentionOpen && canPostChat && (e.key === '@' || e.key === '＠')) {
                                            setMentionOpen(true);
                                        }
                                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                />
                                <Button
                                    size="icon"
                                    className="rounded-full"
                                    onClick={handleSend}
                                    disabled={uploading || (!input.trim() && !attachment)}
                                    style={{ backgroundColor: chatTheme.colors.primary, color: primaryTextColor }}
                                >
                                    <Send size={18} />
                                </Button>
                            </div>
                            {stampOpen && (
                                <div
                                    className="mt-2 w-full rounded-xl border p-3 shadow-sm"
                                    style={{
                                        borderColor: chatTheme.colors.border,
                                        backgroundColor: withAlpha(chatTheme.colors.card, 0.96),
                                        color: chatTheme.colors.text,
                                    }}
                                >
                                    <div className="mb-3 flex items-center justify-between gap-2">
                                        <div className="text-xs font-semibold" style={{ color: chatTheme.colors.text }}>スタンプ</div>
                                        <button
                                            type="button"
                                            className="rounded-full border p-1"
                                            style={{ borderColor: chatTheme.colors.border, color: mutedTextColor }}
                                            onClick={() => {
                                                stampAutoOpenRef.current = false;
                                                setStampOpen(false);
                                            }}
                                            aria-label="スタンプパネルを閉じる"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    {stampSuggestions.length > 0 && (
                                        <div className="mb-3 rounded-lg border border-gray-100">
                                            <button
                                                type="button"
                                                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                                                onClick={() => setStampSuggestionsExpanded((prev) => !prev)}
                                                aria-expanded={stampSuggestionsExpanded}
                                            >
                                                <div>
                                                    <div className="text-xs font-semibold text-gray-700">予測スタンプ</div>
                                                    <div className="text-[11px] text-gray-500">入力内容から候補を提案します</div>
                                                </div>
                                                <div className="flex items-center gap-1 text-[11px] font-medium text-gray-500">
                                                    <span>{stampSuggestionsExpanded ? '閉じる' : '開く'}</span>
                                                    {stampSuggestionsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                </div>
                                            </button>
                                            {stampSuggestionsExpanded && (
                                                <div className="border-t border-gray-100 px-3 py-3">
                                                    <div className="grid grid-cols-5 gap-2">
                                                        {stampSuggestions.slice(0, STAMP_PAGE_SIZE).map((stamp) => (
                                                            <button
                                                                key={`suggest-${stamp.id}`}
                                                                type="button"
                                                                className="rounded border border-gray-100 bg-white hover:bg-gray-50 p-1"
                                                                onClick={() => handleSelectStamp(stamp)}
                                                            >
                                                                <img src={stamp.src} alt={`stamp-${stamp.id}`} className="w-full h-full aspect-square object-contain" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="rounded-lg border border-gray-100">
                                        <button
                                            type="button"
                                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                                            onClick={() => setStampListExpanded((prev) => !prev)}
                                            aria-expanded={stampListExpanded}
                                        >
                                            <div>
                                                <div className="text-xs font-semibold text-gray-700">スタンプ一覧</div>
                                                <div className="text-[11px] text-gray-500">最近・よく使う・PT から選べます</div>
                                            </div>
                                            <div className="flex items-center gap-1 text-[11px] font-medium text-gray-500">
                                                <span>{stampListExpanded ? '閉じる' : '開く'}</span>
                                                {stampListExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </div>
                                        </button>
                                        {stampListExpanded && (
                                            <div className="border-t border-gray-100 px-3 py-3">
                                                <div className="mb-2 flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className={`px-3 py-1 rounded-full text-xs border ${
                                                            stampTab === 'recent'
                                                                ? 'bg-gray-900 text-white border-gray-900'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                        onClick={() => { setStampTab('recent'); setStampPage(0); }}
                                                    >
                                                        最近
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`px-3 py-1 rounded-full text-xs border ${
                                                            stampTab === 'frequent'
                                                                ? 'bg-gray-900 text-white border-gray-900'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                        onClick={() => { setStampTab('frequent'); setStampPage(0); }}
                                                    >
                                                        よく使う
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`px-3 py-1 rounded-full text-xs border ${
                                                            stampTab === 'pt'
                                                                ? 'bg-gray-900 text-white border-gray-900'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                        onClick={() => { setStampTab('pt'); setStampPage(0); }}
                                                    >
                                                        PT
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-5 gap-2">
                                                    {stampPageItems.length === 0 ? (
                                                        <div className="col-span-5 text-center text-xs text-gray-400 py-4">
                                                            スタンプがありません。
                                                        </div>
                                                    ) : (
                                                        stampPageItems.map((stamp) => (
                                                            <button
                                                                key={`page-${stamp.id}`}
                                                                type="button"
                                                                className="rounded border border-gray-100 bg-white hover:bg-gray-50 p-1"
                                                                onClick={() => handleSelectStamp(stamp)}
                                                            >
                                                                <img src={stamp.src} alt={`stamp-${stamp.id}`} className="w-full h-full aspect-square object-contain" />
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
                                                        onClick={() => setStampPage((prev) => Math.max(0, prev - 1))}
                                                        disabled={stampPageCount <= 1 || clampedStampPage <= 0}
                                                    >
                                                        ◀︎
                                                    </button>
                                                    <span>{stampPageCount === 0 ? '0/0' : `${clampedStampPage + 1}/${stampPageCount}`}</span>
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
                                                        onClick={() => setStampPage((prev) => Math.min(stampPageCount - 1, prev + 1))}
                                                        disabled={stampPageCount <= 1 || clampedStampPage >= stampPageCount - 1}
                                                    >
                                                        ▶︎
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="text-[11px] mt-1" style={{ color: mutedTextColor }}>
                                Enterで改行 / Ctrl+Enterで送信 ・ 送信後30秒以内であれば送信取り消し可能
                            </div>
                        </div>
                    ) : (
                        <div
                            className="p-3 md:p-4 border-t text-sm"
                            style={{
                                ...panelStyle,
                                borderColor: withAlpha(chatTheme.colors.border, 0.7),
                                color: mutedTextColor,
                            }}
                        >
                            このグループはアナウンス専用のため、閲覧のみです。
                        </div>
                    )}

                    {mentionOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                            <div
                                className="rounded-lg shadow-lg w-full max-w-md p-5 space-y-4 border"
                                style={{
                                    backgroundColor: withAlpha(chatTheme.colors.card, 0.98),
                                    borderColor: chatTheme.colors.border,
                                    color: chatTheme.colors.text,
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">メンションするユーザー</div>
                                    <button onClick={() => closeMentionModal('cancel')}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <input
                                    ref={mentionInputRef}
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    style={{
                                        borderColor: chatTheme.colors.border,
                                        backgroundColor: inputSurface,
                                        color: chatTheme.colors.text,
                                    }}
                                    placeholder="表示名で検索"
                                    value={mentionSearch}
                                    onChange={(e) => setMentionSearch(e.target.value)}
                                />
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={mentionAll}
                                        onChange={() => {
                                            setMentionAll(!mentionAll);
                                            if (!mentionAll) {
                                                setMentionUserIds(new Set());
                                            }
                                        }}
                                    />
                                    <span>@All</span>
                                </label>
                                <div className="max-h-56 overflow-y-auto space-y-2">
                                    {filteredMembers.map((member) => (
                                        <label key={member.user_id} className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={mentionUserIds.has(member.user_id)}
                                                onChange={() => toggleMentionUser(member.user_id)}
                                                disabled={mentionAll}
                                            />
                                            <span>{resolveName(member.display_name)}</span>
                                        </label>
                                    ))}
                                </div>
                                <Button className="w-full" style={{ backgroundColor: chatTheme.colors.primary, color: primaryTextColor }} onClick={() => closeMentionModal('confirm')}>
                                    完了
                                </Button>
                            </div>
                        </div>
                    )}

                    {muteOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                            <div
                                className="rounded-lg shadow-lg w-full max-w-lg p-5 space-y-4 border"
                                style={{
                                    backgroundColor: withAlpha(chatTheme.colors.card, 0.98),
                                    borderColor: chatTheme.colors.border,
                                    color: chatTheme.colors.text,
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">ミュート設定</div>
                                    <button onClick={() => setMuteOpen(false)}>
                                        <X size={16} />
                                    </button>
                                </div>
                                {muteLoading ? (
                                    <div className="text-sm" style={{ color: mutedTextColor }}>読み込み中...</div>
                                ) : (
                                    <>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={muteAll}
                                                onChange={(e) => setMuteAll(e.target.checked)}
                                            />
                                            <span>このグループをミュート</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={muteNight}
                                                onChange={(e) => setMuteNight(e.target.checked)}
                                            />
                                            <span>このグループを夜間のみミュート（22:00〜7:00）</span>
                                        </label>
                                        <div className="border-t pt-3 space-y-2">
                                            <div className="text-sm font-semibold">特定のユーザーの投稿のみミュート</div>
                                            <input
                                                className="w-full border rounded px-3 py-2 text-sm"
                                                style={{
                                                    borderColor: chatTheme.colors.border,
                                                    backgroundColor: inputSurface,
                                                    color: chatTheme.colors.text,
                                                }}
                                                placeholder="表示名で検索"
                                                value={muteSearch}
                                                onChange={(e) => setMuteSearch(e.target.value)}
                                            />
                                            <label className="flex items-center gap-2 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={muteCandidates.length > 0 && muteCandidates.every((m) => mutedUserIds.has(m.user_id))}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setMutedUserIds((prev) => {
                                                            const next = new Set(prev);
                                                            if (checked) {
                                                                muteCandidates.forEach((m) => next.add(m.user_id));
                                                            } else {
                                                                muteCandidates.forEach((m) => next.delete(m.user_id));
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                />
                                                <span>すべて選択</span>
                                            </label>
                                            <div
                                                className="max-h-56 overflow-y-auto space-y-2 border rounded p-2"
                                                style={{ borderColor: chatTheme.colors.border, backgroundColor: inputSurface }}
                                            >
                                                {filteredMuteMembers.length === 0 ? (
                                                    <div className="text-xs" style={{ color: mutedTextColor }}>該当ユーザーがいません。</div>
                                                ) : (
                                                    filteredMuteMembers.map((member) => (
                                                        <label key={member.user_id} className="flex items-center gap-2 text-sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={mutedUserIds.has(member.user_id)}
                                                                onChange={() => toggleMutedUser(member.user_id)}
                                                            />
                                                            <span>{resolveName(member.display_name)}</span>
                                                        </label>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                        {muteError && <div className="text-xs text-red-500">{muteError}</div>}
                                        <div className="flex justify-end gap-2 pt-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => setMuteOpen(false)}
                                                disabled={muteSaving}
                                                style={{ borderColor: chatTheme.colors.border, color: chatTheme.colors.text }}
                                            >
                                                キャンセル
                                            </Button>
                                            <Button
                                                onClick={async () => {
                                                    setMuteSaving(true);
                                                    setMuteError('');
                                                    try {
                                                        await updateChatMuteSettings(group.group_id, {
                                                            mute_all: muteAll,
                                                            mute_night: muteNight,
                                                            muted_user_ids: Array.from(mutedUserIds),
                                                        });
                                                        setMuteOpen(false);
                                                    } catch (err) {
                                                        console.error('Failed to save mute settings', err);
                                                        setMuteError('保存に失敗しました。');
                                                    } finally {
                                                        setMuteSaving(false);
                                                    }
                                                }}
                                                disabled={muteSaving}
                                                style={{ backgroundColor: chatTheme.colors.primary, color: primaryTextColor }}
                                            >
                                                {muteSaving ? '保存中...' : '保存'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {reactionModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                            <div
                                className="rounded-lg shadow-lg w-full max-w-sm p-5 space-y-3 border"
                                style={{
                                    backgroundColor: withAlpha(chatTheme.colors.card, 0.98),
                                    borderColor: chatTheme.colors.border,
                                    color: chatTheme.colors.text,
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">いいね一覧</div>
                                    <button onClick={() => setReactionModal(null)}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="max-h-64 overflow-y-auto space-y-2">
                                    {reactionModal.reactions.length === 0 ? (
                                        <div className="text-sm" style={{ color: mutedTextColor }}>まだリアクションはありません。</div>
                                    ) : (
                                        reactionModal.reactions.map((reaction, index) => (
                                            <div key={`${reaction.user_id}-${index}`} className="flex items-center gap-2 text-sm">
                                                <span>{reaction.emoji}</span>
                                                <Avatar
                                                    username={resolveName(reaction.display_name)}
                                                    src={reaction.avatar_path}
                                                    size="sm"
                                                    theme={getTheme('User', 3)}
                                                    onClick={() => handleResidentProfileNavigate(reaction.user_id)}
                                                />
                                                <span>{resolveName(reaction.display_name)}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                    <div className="max-w-3xl mx-auto space-y-4">
                        <div className="flex flex-wrap gap-2">
                            {THEATER_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={`px-3 py-1 text-sm rounded-full ${
                                        theaterTab === tab.key ? 'bg-[#800020] text-white' : 'text-gray-500 border border-gray-200'
                                    }`}
                                    onClick={() => setTheaterTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="rounded-lg border bg-white p-4 space-y-3">
                            <div className="text-sm font-semibold text-gray-700">絞り込み</div>
                            <div className="grid gap-2 md:grid-cols-3">
                                <input
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    placeholder="タイトルで検索"
                                    value={theaterFilterTitle}
                                    onChange={(e) => setTheaterFilterTitle(e.target.value)}
                                />
                                <input
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    type="date"
                                    value={theaterFilterFrom}
                                    onChange={(e) => setTheaterFilterFrom(e.target.value)}
                                />
                                <input
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    type="date"
                                    value={theaterFilterTo}
                                    onChange={(e) => setTheaterFilterTo(e.target.value)}
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" onClick={applyTheaterFilters}>
                                    絞り込み
                                </Button>
                                <Button size="sm" variant="outline" onClick={resetTheaterFilters}>
                                    リセット
                                </Button>
                            </div>
                            {hasActiveTheaterFilters && (
                                <div className="text-xs text-gray-500">
                                    フォルダ指定は無視して検索されます。
                                </div>
                            )}
                        </div>

                        {!hasActiveTheaterFilters && (
                            <div className="rounded-lg border bg-white p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold text-gray-700">フォルダ</div>
                                    {theaterActiveFolderId !== null && (
                                        <button
                                            className="text-xs text-gray-500 hover:text-[#800020]"
                                            onClick={() => setTheaterActiveFolderId(currentFolder?.parent_id ?? null)}
                                        >
                                            ← 戻る
                                        </button>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    現在: {theaterActiveFolderId === null ? 'トップ' : getFolderPath(theaterActiveFolderId)}
                                </div>
                                {currentSubfolders.length === 0 ? (
                                    <div className="text-xs text-gray-500">フォルダはありません。</div>
                                ) : (
                                    <div className="space-y-2">
                                        {currentSubfolders.map((folder) => (
                                            <button
                                                key={folder.folder_id}
                                                className="w-full text-left border rounded px-3 py-2 text-sm hover:bg-gray-50"
                                                onClick={() => setTheaterActiveFolderId(folder.folder_id)}
                                            >
                                                📁 {folder.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {isStaff && (
                                    <div className="space-y-2 border-t pt-3">
                                        <div className="text-xs font-semibold text-gray-600">フォルダを作成</div>
                                        <input
                                            className="w-full border rounded px-3 py-2 text-sm"
                                            placeholder="フォルダ名"
                                            value={theaterFolderName}
                                            onChange={(e) => setTheaterFolderName(e.target.value)}
                                        />
                                        <select
                                            className="w-full border rounded px-3 py-2 text-sm"
                                            value={theaterFolderParent}
                                            onChange={(e) => setTheaterFolderParent(e.target.value)}
                                        >
                                            <option value="root">ルート（最上位）</option>
                                            {folderOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <Button size="sm" onClick={handleCreateFolder} disabled={!theaterFolderName.trim()}>
                                            フォルダを作成
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {theaterLoading && <div className="text-sm text-gray-500">読み込み中...</div>}

                        {canTheaterPost && theaterTab === 'text' && (
                            <div className="space-y-2 rounded-lg border bg-white p-4">
                                <div className="text-sm font-semibold text-gray-700">テキスト投稿</div>
                                <input
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    placeholder="タイトル（任意）"
                                    value={theaterTextTitle}
                                    onChange={(e) => setTheaterTextTitle(e.target.value)}
                                />
                                <select
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    value={theaterSaveFolder}
                                    onChange={(e) => setTheaterSaveFolder(e.target.value)}
                                >
                                    <option value="none">保存先: 未分類</option>
                                    {folderOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            保存先: {option.label}
                                        </option>
                                    ))}
                                </select>
                                <textarea
                                    ref={theaterTextRef}
                                    className="w-full border rounded p-2 text-sm"
                                    rows={4}
                                    value={theaterText}
                                    onChange={(e) => setTheaterText(e.target.value)}
                                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                    onPaste={handleTheaterTextPaste}
                                    placeholder="シアター用のテキストを入力"
                                />
                                <Button onClick={handleTheaterTextSubmit} disabled={!theaterText.trim()}>
                                    投稿する
                                </Button>
                            </div>
                        )}

                        {canTheaterPost && (theaterTab === 'image' || theaterTab === 'media') && (
                            <div className="space-y-2 rounded-lg border bg-white p-4">
                                <div className="text-sm font-semibold text-gray-700">メディア投稿</div>
                                <div className="text-[11px] leading-relaxed text-gray-500">
                                    AI判定 基準: 画像は JPEG / PNG / WebP / GIF、動画は音声あり約45分・音声なし約60分、音声は約8.4時間まで
                                </div>
                                {theaterUploadError && <div className="text-xs text-red-600">{theaterUploadError}</div>}
                                <input
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    placeholder="タイトル（任意）"
                                    value={theaterMediaTitle}
                                    onChange={(e) => setTheaterMediaTitle(e.target.value)}
                                />
                                <select
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    value={theaterSaveFolder}
                                    onChange={(e) => setTheaterSaveFolder(e.target.value)}
                                >
                                    <option value="none">保存先: 未分類</option>
                                    {folderOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            保存先: {option.label}
                                        </option>
                                    ))}
                                </select>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => theaterFileInputRef.current?.click()}
                                        disabled={theaterUploading}
                                    >
                                        ファイルを選択
                                    </Button>
                                    {theaterUploading && (
                                        <div className="flex flex-col gap-1 text-xs text-gray-500">
                                            <span>
                                                {theaterUploadProgress !== null && theaterUploadProgress < 100
                                                    ? `アップロード中... ${theaterUploadProgress}%`
                                                    : '変換中...'}
                                            </span>
                                            {theaterUploadProgress !== null && (
                                                <div className="h-1 w-36 rounded-full bg-gray-200 overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#800020]"
                                                        style={{ width: `${theaterUploadProgress}%` }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={theaterFileInputRef}
                                    type="file"
                                    accept={theaterTab === 'image' ? 'image/*' : 'video/*,audio/*'}
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            handleTheaterUpload(file);
                                        }
                                        e.currentTarget.value = '';
                                    }}
                                />
                                {theaterUpload && (
                                    <div className="rounded border bg-gray-50 p-2 text-xs text-gray-600">
                                        {theaterTab === 'image' ? (
                                            <img src={theaterUpload.url} alt="preview" className="max-h-48 rounded" />
                                        ) : theaterUpload.contentType.startsWith('audio/') ? (
                                            <audio src={theaterUpload.url} controls />
                                        ) : theaterUpload.thumbUrl ? (
                                            <img src={theaterUpload.thumbUrl} alt="preview" className="max-h-48 rounded" />
                                        ) : (
                                            <video src={theaterUpload.url} controls preload="metadata" className="max-h-48 rounded" />
                                        )}
                                    </div>
                                )}
                                <Button onClick={handleTheaterMediaSubmit} disabled={!theaterUpload}>
                                    投稿する
                                </Button>
                            </div>
                        )}

                        {isStaff && theaterTab === 'note' && (
                            <div className="rounded-lg border bg-white p-4 text-sm text-gray-600">
                                チャットのメッセージから「ノート」を選ぶと、ここに保存されます。
                            </div>
                        )}

                        <div className="space-y-3">
                            {hasActiveTheaterFilters ? (
                                <>
                                    {theaterItems.length === 0 && !theaterLoading && (
                                        <div className="text-sm text-gray-500">該当する投稿はありません。</div>
                                    )}
                                    {theaterItems.map((item: any) => renderTheaterItemCard(item, true))}
                                </>
                            ) : theaterActiveFolderId === null ? (
                                <>
                                    {theaterItems.length === 0 && !theaterLoading && (
                                        <div className="text-sm text-gray-500">未分類の投稿はまだありません。</div>
                                    )}
                                    {theaterItems.map((item: any) => renderTheaterItemCard(item, false))}
                                </>
                            ) : (
                                <>
                                    <div className="text-sm font-semibold text-gray-700">
                                        {getFolderPath(theaterActiveFolderId)}
                                    </div>
                                    {theaterItems.length === 0 && !theaterLoading && (
                                        <div className="text-sm text-gray-500">このフォルダには投稿がありません。</div>
                                    )}
                                    {theaterItems.map((item: any) => renderTheaterItemCard(item, false))}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <TranslateModal
                isOpen={translateOpen}
                onClose={() => setTranslateOpen(false)}
                sourceText={translateSource}
            />
        </div>
    );
}
